import { useTranslation } from 'react-i18next';
//frontend/src/services/api.js - ENHANCED: Complete multi-company API service with sync and unit preferences
import i18n from '../i18n/config';
import axios from 'axios';
import { useState, useCallback } from 'react';

// Create axios instance with default configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8010';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      localStorage.removeItem('companies');
      localStorage.removeItem('currentCompany');
      window.location.href = '/login';
    }
    if (error.response?.status === 402) {
      const detail = error.response?.data?.detail;
      if (detail?.code === 'COMPANY_BLOCKED_FOR_BILLING') {
        window.location.href = '/billing/portal';
      }
    }
    return Promise.reject(error);
  }
);

// Helper function to validate timezone
function isValidTimezone(timezone) {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

const cleanParams = (params) => {
  const cleaned = {};
  Object.keys(params).forEach(key => {
    const value = params[key];
    if (value !== null &&
      value !== undefined &&
      value !== '' &&
      value !== 'undefined' &&
      value !== 'null') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const normalizeRegistrationLookupUser = (user, fallbackEmail = '') => {
  if (!user) {
    return null;
  }

  const firstName = user.firstName || user.first_name || user.name || '';
  const lastName = user.lastName || user.last_name || '';
  const normalizedEmail = String(user.email || fallbackEmail || '').trim().toLowerCase();
  const fullName = user.fullName || user.full_name || [firstName, lastName].filter(Boolean).join(' ').trim() || normalizedEmail;

  return {
    id: user.id || null,
    email: normalizedEmail,
    firstName,
    lastName,
    fullName,
    language: user.language || 'es',
    timezone: user.timezone || null,
  };
};

// Profile utilities for alarm profiles
export const profileUtils = {
  getDefaultTPMSSettings() {
    return {
      channels_by_severity: {
        alarm: { portal: true, email: true, whatsapp: false, voice: false, voice_delay_minutes: null, voice_policy: 'disabled' },
        critical: { portal: true, email: true, whatsapp: true, voice: true, voice_delay_minutes: 5, voice_policy: 'persistent' },
        extreme: { portal: true, email: true, whatsapp: true, voice: true, voice_delay_minutes: 0, voice_policy: 'immediate' }
      },
      dual_yoyo: {
        enabled: true,
        // Método fijo: promedio. No editable por usuario.
        comparison_method: 'average',
        // Severidad base fija: alerta (Atención). No editable por usuario.
        default_severity: 'alarm',
        // Defaults % (sobreescribibles desde el panel Arsenal vía getProfileTemplate())
        allowed_difference_percent: 5,
        critical_difference_percent: 10,
        extreme_difference_percent: 15
      },
      // Defaults de no-reporte (administrables desde el panel Arsenal).
      no_report_sensor: { enabled: true, value: 48, unit: 'hours' },
      no_report_receiver: { enabled: true, value: 24, unit: 'hours' },
      // Defaults de presión por severidad — % positivos. El frontend los convierte:
      //   low_percent → severity.low = −low_percent  (caída respecto al ideal en frío)
      //   high_percent → severity.high = +high_percent
      // Estos defaults también vienen del backend (getProfileTemplate) y los sobrescriben.
      pressure_levels_default: {
        alarm:    { low_percent: 10, high_percent: 5 },
        critical: { low_percent: 15, high_percent: 10 },
        extreme:  { low_percent: 20, high_percent: 15 }
      },
      thermal_risk: {
        enabled: true,
        axle_delta_celsius: 20,
        rate_enabled: true,
        rate_celsius_per_minute: 2,
        rate_window_minutes: 5,
        pressure_rise_enabled: false,
        pressure_rise_percent: 10,
        default_severity: 'critical'
      },
      temperature: {
        category: 'CUSTOM'
      }
    };
  },

  getUnitsForMetric(metricType) {
    const units = {
      'PSI': ['PSI', 'BAR', 'KPA'],
      'TEMP': ['C', 'F', 'K'],
      'ACC': ['G', 'M/S2', 'FT/S2']
    };
    return units[metricType] || ['PSI'];
  },

  getDefaultThresholds(metricType) {
    const defaults = {
      'PSI': {
        min: 90,
        max: 135,
        ideal: 120,
        unit: 'PSI',
        advanced_settings: {
          pressure: {
            close_range_percent: { low: -3, high: 15 },
            severity_levels: {
              alarm: { low: -25, high: 12.5 },
              critical: { low: -30, high: 22.5 },
              extreme: { low: -35, high: 32.5 }
            }
          }
        }
      },
      'TEMP': {
        min: null,
        max: 75,
        ideal: 65,
        unit: 'C',
        advanced_settings: {
          temperature: {
            category: 'CUSTOM'
          }
        }
      },
      'ACC': { min: null, max: 3, ideal: null, unit: 'G' }
    };
    return defaults[metricType] || { min: null, max: null, ideal: null, unit: 'PSI' };
  },

  getDefaultAdvancedSettings(metricType, config = {}, tpmsSettings = null) {
    const defaults = this.getDefaultThresholds(metricType);
    const ideal = Number(config.ideal_value ?? defaults.ideal ?? 0) || 0;

    if (metricType === 'PSI') {
      // Defaults globales por severidad. Si vienen en tpmsSettings (cargados del
      // template del backend), se usan; si no, se usa el fallback hardcoded.
      // Estructura: { alarm: {low_percent, high_percent}, critical: {...}, extreme: {...} }
      const lvlDefaults = (tpmsSettings && tpmsSettings.pressure_levels_default) || {
        alarm:    { low_percent: 10, high_percent: 5 },
        critical: { low_percent: 15, high_percent: 10 },
        extreme:  { low_percent: 20, high_percent: 15 }
      };

      const fromPct = (p) => (ideal ? Number((ideal * (1 + Number(p || 0) / 100)).toFixed(2)) : null);
      const buildLevel = (sevDef) => {
        const low = -Math.abs(Number(sevDef.low_percent || 0));
        const high = Math.abs(Number(sevDef.high_percent || 0));
        return {
          low,
          high,
          // Valores absolutos calculados respecto al ideal en frío.
          low_value: fromPct(low),
          high_value: fromPct(high),
          mode: 'percent',
        };
      };

      // Cierre automático de ticket — defaults: -3% mínimo, +15% máximo (respecto al ideal en frío).
      // Si en el futuro el admin Arsenal quiere editarlo desde "Defaults TPMS", basta
      // con leer tpmsSettings.pressure_close_range_default; por ahora hardcoded.
      const closeDef = (tpmsSettings && tpmsSettings.pressure_close_range_default) || { low_percent: 3, high_percent: 15 };
      const closeLowSigned = -Math.abs(Number(closeDef.low_percent || 0));
      const closeHighSigned = Math.abs(Number(closeDef.high_percent || 0));

      return {
        pressure: {
          close_range_percent: { low: closeLowSigned, high: closeHighSigned },
          // Valor absoluto calculado para mostrar el campo "Valor" pre-llenado.
          close_range_value: {
            low: fromPct(closeLowSigned),
            high: fromPct(closeHighSigned),
          },
          severity_levels: {
            alarm: buildLevel(lvlDefaults.alarm),
            critical: buildLevel(lvlDefaults.critical),
            extreme: buildLevel(lvlDefaults.extreme),
          }
        }
      };
    }
    const max = Number(config.threshold_max ?? defaults.max ?? 0) || 0; // sólo TEMP usa max

    if (metricType === 'TEMP') {
      return {
        temperature: {
          category: 'CUSTOM',
          thresholds: {
            alarm: Number(config.threshold_max ?? defaults.max ?? 75),
            critical: Number(config.threshold_max ?? defaults.max ?? 75) + 10,
            extreme: Number(config.threshold_max ?? defaults.max ?? 75) + 35,
          },
          safe_below: Math.max((Number(config.threshold_max ?? defaults.max ?? 75) - 5), Number(config.ideal_value ?? defaults.ideal ?? 65))
        }
      };
    }

    return {};
  },

  normalizeMetricConfig(metricType, config = {}) {
    const defaults = this.getDefaultThresholds(metricType);
    return {
      enabled: config.enabled !== false,
      threshold_min: config.threshold_min ?? defaults.min ?? null,
      threshold_max: config.threshold_max ?? defaults.max ?? null,
      ideal_value: config.ideal_value ?? defaults.ideal ?? null,
      unit: config.unit || defaults.unit || 'PSI',
      warning_threshold_percentage: config.warning_threshold_percentage ?? 90,
      cooldown_minutes: config.cooldown_minutes ?? 5,
      advanced_settings: config.advanced_settings || this.getDefaultAdvancedSettings(metricType, config)
    };
  },

  buildPreview(metricType, config = {}, tpmsSettings = {}) {
    const normalized = this.normalizeMetricConfig(metricType, config);

    if (metricType === 'PSI') {
      const ideal = Number(normalized.ideal_value || 0);
      const advanced = normalized.advanced_settings?.pressure || {};
      const levels = advanced.severity_levels || {};
      const closeRange = advanced.close_range_percent || { low: -3, high: 15 };
      const fromPct = (pct) => (ideal ? Number((ideal * (1 + Number(pct || 0) / 100)).toFixed(2)) : null);

      return {
        type: 'pressure',
        ideal,
        close_zone: {
          min: fromPct(closeRange.low),
          max: fromPct(closeRange.high)
        },
        severities: Object.fromEntries(
          Object.entries(levels).map(([severity, values]) => [severity, {
            low: fromPct(values.low),
            high: fromPct(values.high)
          }])
        )
      };
    }

    if (metricType === 'TEMP') {
      const advanced = normalized.advanced_settings?.temperature || {};
      return {
        type: 'temperature',
        category: tpmsSettings?.temperature?.category || advanced.category || 'CUSTOM',
        thresholds: advanced.thresholds || {
          alarm: normalized.threshold_max,
          critical: Number(normalized.threshold_max || 0) + 10,
          extreme: Number(normalized.threshold_max || 0) + 35,
        },
        safe_below: advanced.safe_below ?? Math.max((Number(normalized.threshold_max || 0) - 5), Number(normalized.ideal_value || 0))
      };
    }

    return null;
  },

  validateMetricConfig(config) {
    const errors = [];
    
    if (!config.metric_type) {
      errors.push(i18n.t('hardcoded.services_api.tipo_metrica_requerido'));
    }
    if (!config.unit) {
      errors.push(i18n.t('hardcoded.services_api.unidad_requerida'));
    }
    if (config.threshold_min !== null && config.threshold_max !== null) {
      if (config.threshold_min >= config.threshold_max) {
        errors.push(i18n.t('hardcoded.services_api.min_debe_ser_menor_que_max'));
      }
    }
    if (config.ideal_value !== null) {
      if (config.threshold_min !== null && config.ideal_value < config.threshold_min) {
        errors.push(i18n.t('hardcoded.services_api.ideal_debe_ser_mayor_o_igual_min'));
      }
      if (config.threshold_max !== null && config.ideal_value > config.threshold_max) {
        errors.push(i18n.t('hardcoded.services_api.ideal_debe_ser_menor_o_igual_max'));
      }
    }
    if (config.axle_number < 1) {
      errors.push(i18n.t('hardcoded.services_api.eje_debe_ser_mayor_a_0'));
    }

    const advanced = config.advanced_settings || {};
    if (config.metric_type === 'PSI' && advanced.pressure?.close_range_percent) {
      const closeRange = advanced.pressure.close_range_percent;
      if (Number(closeRange.low) >= Number(closeRange.high)) {
        errors.push(i18n.t('hardcoded.services_api.zona_cierre_low_menor_high'));
      }
    }
    if (config.metric_type === 'TEMP' && advanced.temperature?.thresholds) {
      const thresholds = advanced.temperature.thresholds;
      if (!(Number(thresholds.alarm) < Number(thresholds.critical) && Number(thresholds.critical) <= Number(thresholds.extreme))) {
        errors.push(i18n.t('hardcoded.services_api.umbrales_temp_deben_cumplir'));
      }
      if (advanced.temperature.safe_below != null && Number(advanced.temperature.safe_below) >= Number(thresholds.alarm)) {
        errors.push(i18n.t('hardcoded.services_api.zona_segura_temp_debe_estar_debajo'));
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  generateConfigurations(profileData, metricsConfig, axleSpecificConfigs = {}) {
    const configurations = [];
    const enabledMetrics = Object.keys(metricsConfig).filter(
      metric => metricsConfig[metric].enabled
    );

    if (profileData.applies_to_all_axles) {
      enabledMetrics.forEach(metricType => {
        const config = metricsConfig[metricType];
        configurations.push({
          axle_number: 1,
          metric_type: metricType,
          threshold_min: config.threshold_min,
          threshold_max: config.threshold_max,
          ideal_value: config.ideal_value,
          unit: config.unit,
          enabled: true,
          advanced_settings: config.advanced_settings || this.getDefaultAdvancedSettings(metricType, config),
          warning_threshold_percentage: 90.0,
          cooldown_minutes: 5
        });
      });
    } else {
      for (let axle = 1; axle <= profileData.axle_count; axle++) {
        enabledMetrics.forEach(metricType => {
          const specificKey = `${axle}-${metricType}`;
          const specificConfig = axleSpecificConfigs[specificKey];
          const baseConfig = metricsConfig[metricType];

          if (specificConfig?.enabled === false) {
            return;
          }

          configurations.push({
            axle_number: axle,
            metric_type: metricType,
            threshold_min: specificConfig?.threshold_min ?? baseConfig.threshold_min,
            threshold_max: specificConfig?.threshold_max ?? baseConfig.threshold_max,
            ideal_value: specificConfig?.ideal_value ?? baseConfig.ideal_value,
            unit: specificConfig?.unit ?? baseConfig.unit,
            enabled: true,
            advanced_settings: specificConfig?.advanced_settings || baseConfig.advanced_settings || this.getDefaultAdvancedSettings(metricType, specificConfig || baseConfig),
            warning_threshold_percentage: 90.0,
            cooldown_minutes: 5
          });
        });
      }
    }

    return configurations;
  },

  // Nuevas funciones para valores ideales
  isValueIdeal(value, idealValue, marginPercentage = 2.0) {
    if (!idealValue) return null;
    const marginRange = idealValue * (marginPercentage / 100.0);
    return Math.abs(value - idealValue) <= marginRange;
  },

  getIdealDeviation(value, idealValue) {
    if (!idealValue) return null;
    const deviation = ((value - idealValue) / idealValue) * 100;
    return deviation.toFixed(2);
  },

  formatIdealStatus(value, idealValue, marginPercentage = 2.0) {
    if (!idealValue) return { status: 'unknown', message: i18n.t('hardcoded.services_api.sin_valor_ideal') };
    
    const isIdeal = this.isValueIdeal(value, idealValue, marginPercentage);
    const deviation = this.getIdealDeviation(value, idealValue);
    
    if (isIdeal) {
      return { 
        status: 'ideal', 
        message: `Valor ideal (${deviation}% del ideal)`,
        color: 'success'
      };
    } else {
      return { 
        status: 'not-ideal', 
        message: `Fuera del ideal (${deviation}% del ideal)`,
        color: 'warning'
      };
    }
  },

  convertTemperature(value, fromUnit, toUnit) {
    if (fromUnit === toUnit) return value;
    let celsius = value;
    if (fromUnit === 'F') {
      celsius = (value - 32) * 5 / 9;
    } else if (fromUnit === 'K') {
      celsius = value - 273.15;
    }
    if (toUnit === 'F') {
      return celsius * 9 / 5 + 32;
    } else if (toUnit === 'K') {
      return celsius + 273.15;
    }
    return celsius;
  },

  convertPressure(value, fromUnit, toUnit) {
    if (fromUnit === toUnit) return value;
    let psi = value;
    if (fromUnit === 'BAR') {
      psi = value * 14.5038;
    } else if (fromUnit === 'KPA') {
      psi = value * 0.145038;
    }
    if (toUnit === 'BAR') {
      return psi / 14.5038;
    } else if (toUnit === 'KPA') {
      return psi / 0.145038;
    }
    return psi;
  },

  convertAcceleration(value, fromUnit, toUnit) {
    if (fromUnit === toUnit) return value;
    let g = value;
    if (fromUnit === 'M/S2') {
      g = value / 9.80665;
    } else if (fromUnit === 'FT/S2') {
      g = value / 32.174;
    }
    if (toUnit === 'M/S2') {
      return g * 9.80665;
    } else if (toUnit === 'FT/S2') {
      return g * 32.174;
    }
    return g;
  },

  formatMetricValue(value, metricType, unit) {
    if (value === null || value === undefined) return 'N/A';
    const formatted = typeof value === 'number' ? value.toFixed(1) : value;
    return `${formatted} ${unit}`;
  },

  getMetricDisplayName(metricType) {
    const names = {
      'PSI': i18n.t('hardcoded.services_api.metric_presion'),
      'TEMP': i18n.t('hardcoded.services_api.metric_temperatura'),
      'ACC': i18n.t('hardcoded.services_api.metric_acelerometro')
    };
    return names[metricType] || metricType;
  },

  getMetricIcon(metricType) {
    const icons = {
      'PSI': 'PressureIcon',
      'TEMP': 'TempIcon',
      'ACC': 'SpeedIcon'
    };
    return icons[metricType] || 'WarningIcon';
  }
};

const serializeAlarmProfilePayload = (data = {}) => {
  const tpmsSettings = data.tpms_settings || data.tpmsSettings || profileUtils.getDefaultTPMSSettings();

  return {
    name: data.name,
    description: data.description || null,
    axle_count: parseInt(data.axle_count, 10),
    applies_to_all_axles: Boolean(data.applies_to_all_axles),
    tpms_settings: tpmsSettings,
    configurations: (data.configurations || []).map((config) => ({
      axle_number: parseInt(config.axle_number, 10),
      metric_type: config.metric_type,
      threshold_min: config.threshold_min !== null && config.threshold_min !== undefined && config.threshold_min !== '' ? parseFloat(config.threshold_min) : null,
      threshold_max: config.threshold_max !== null && config.threshold_max !== undefined && config.threshold_max !== '' ? parseFloat(config.threshold_max) : null,
      ideal_value: config.ideal_value !== null && config.ideal_value !== undefined && config.ideal_value !== '' ? parseFloat(config.ideal_value) : null,
      unit: config.unit || 'PSI',
      enabled: Boolean(config.enabled),
      advanced_settings: config.advanced_settings || profileUtils.getDefaultAdvancedSettings(config.metric_type, config),
      warning_threshold_percentage: config.warning_threshold_percentage ? parseFloat(config.warning_threshold_percentage) : 90.0,
      cooldown_minutes: config.cooldown_minutes ? parseInt(config.cooldown_minutes, 10) : 5
    }))
  };
};

// API endpoints
export const api = {
  // ───────────────────────── Asistente IA APS (MiniMax) ─────────────────────────
  // La API key vive solo en backend. El frontend nunca la ve.
  ai: {
    chat: (data) => apiClient.post('/ai/chat', data),
    // Streaming SSE: usa fetch (axios no maneja SSE). Devuelve un async iterator
    // de eventos {type, ...}. onEvent se llama por cada evento.
    streamChat: async (data, { onEvent, signal } = {}) => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('access_token') || '';
      const resp = await fetch(`${API_BASE_URL}/ai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data),
        signal,
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`AI stream HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          const line = part.split('\n').find((l) => l.startsWith('data:'));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (payload === '[DONE]') return;
          try {
            const evt = JSON.parse(payload);
            if (onEvent) onEvent(evt);
          } catch (e) { /* ignore malformed chunk */ }
        }
      }
    },
    listConversations: (limit = 30) => apiClient.get(`/ai/conversations?limit=${limit}`),
    getConversation: (id) => apiClient.get(`/ai/conversations/${id}`),
    createConversation: (data) => apiClient.post('/ai/conversations', data),
    // Memoria
    listMemory: () => apiClient.get('/ai/memory'),
    createMemory: (data) => apiClient.post('/ai/memory', data),
    updateMemory: (id, data) => apiClient.put(`/ai/memory/${id}`, data),
    deleteMemory: (id) => apiClient.delete(`/ai/memory/${id}`),
    // Recomendaciones
    listRecommendations: (params = {}) => {
      const sp = new URLSearchParams(cleanParams(params));
      const qs = sp.toString();
      return apiClient.get(`/ai/recommendations${qs ? `?${qs}` : ''}`);
    },
    generateRecommendations: (data) => apiClient.post('/ai/recommendations/generate', data),
    updateRecommendation: (id, status) => apiClient.put(`/ai/recommendations/${id}`, { status }),
    // Feedback / menciones / settings / estado
    feedback: (data) => apiClient.post('/ai/feedback', data),
    resolveMention: (data) => apiClient.post('/ai/tools/resolve-mention', data),
    getSettings: () => apiClient.get('/ai/settings'),
    updateSettings: (data) => apiClient.put('/ai/settings', data),
    status: () => apiClient.get('/ai/status'),
  },

  // ───────────────────────── Analítica de uso del Chat ─────────────────────────
  chatAnalytics: {
    overview: (params = {}) => {
      const sp = new URLSearchParams(cleanParams(params));
      const qs = sp.toString();
      return apiClient.get(`/chat-analytics/overview${qs ? `?${qs}` : ''}`, { timeout: 120000 });
    },
    exportUrl: (params = {}) => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('access_token') || '';
      const sp = new URLSearchParams(cleanParams({ ...params, token: undefined }));
      const base = `${API_BASE_URL}/chat-analytics/export?${sp.toString()}`;
      // El endpoint acepta Bearer; para descarga directa usamos el header vía fetch en la página.
      return { url: base, token };
    },
  },

  // ──────────────────────── Capacitación ────────────────────────
  // Consumo (cualquier usuario) y administración (Solo Arsenal). El backend
  // resuelve la elegibilidad: aquí nunca se decide qué curso puede verse.
  training: {
    myCourses: () => apiClient.get('/training/my-courses'),
    course: (id) => apiClient.get(`/training/courses/${id}`),
    lesson: (id) => apiClient.get(`/training/lessons/${id}`),
    start: (id) => apiClient.post(`/training/courses/${id}/start`),
    completeLesson: (id) => apiClient.post(`/training/lessons/${id}/complete`),
    toggleChecklist: (blockId, itemKey, checked) =>
      apiClient.post(`/training/blocks/${blockId}/checklist`, { item_key: itemKey, checked }),
    submitBlock: (blockId, payload) => apiClient.post(`/training/blocks/${blockId}/submit`, payload),
    submissions: (blockId) => apiClient.get(`/training/blocks/${blockId}/submissions`),

    admin: {
      list: (params = {}) => apiClient.get('/training/admin/courses', { params }),
      create: (data) => apiClient.post('/training/admin/courses', data),
      update: (id, data) => apiClient.put(`/training/admin/courses/${id}`, data),
      publish: (id, publish = true) =>
        apiClient.post(`/training/admin/courses/${id}/publish?publish=${publish}`),
      archive: (id) => apiClient.delete(`/training/admin/courses/${id}`),
      addSection: (courseId, data) => apiClient.post(`/training/admin/courses/${courseId}/sections`, data),
      addLesson: (sectionId, data) => apiClient.post(`/training/admin/sections/${sectionId}/lessons`, data),
      addBlock: (lessonId, data) => apiClient.post(`/training/admin/lessons/${lessonId}/blocks`, data),
      addQuestion: (blockId, data) => apiClient.post(`/training/admin/blocks/${blockId}/questions`, data),
      upload: (formData) => apiClient.post('/training/admin/uploads', formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }),
      updateBlock: (blockId, data) => apiClient.put(`/training/admin/blocks/${blockId}`, data),
      deleteBlock: (blockId) => apiClient.delete(`/training/admin/blocks/${blockId}`),
      reorderSections: (courseId, ids) => apiClient.put(`/training/admin/courses/${courseId}/sections/reorder`, { ids }),
      reorderLessons: (sectionId, ids) => apiClient.put(`/training/admin/sections/${sectionId}/lessons/reorder`, { ids }),
      reorderBlocks: (lessonId, ids) => apiClient.put(`/training/admin/lessons/${lessonId}/blocks/reorder`, { ids }),
      assignments: (courseId) => apiClient.get(`/training/admin/courses/${courseId}/assignments`),
      addAssignment: (courseId, data) => apiClient.post(`/training/admin/courses/${courseId}/assignments`, data),
      removeAssignment: (id) => apiClient.delete(`/training/admin/assignments/${id}`),
      progress: (params = {}) => apiClient.get('/training/admin/progress', { params }),
      stats: () => apiClient.get('/training/admin/stats'),
      // Vista previa del constructor: dice si la URL se podrá incrustar.
      validateVideo: (url) =>
        apiClient.post(`/training/admin/validate-video?url=${encodeURIComponent(url)}`),
    },
  },

  // ──────────────── Buzón del canal portal (usuario final) ────────────────
  // Lo que el sistema registra como "entregado en el portal" tiene que poder
  // abrirse aquí; si no, la auditoría estaría afirmando algo que no existe.
  portalNotifications: {
    list: (params = {}) => apiClient.get('/portal-notifications', { params }),
    detail: (id) => apiClient.get(`/portal-notifications/${id}`),
    unreadCount: () => apiClient.get('/portal-notifications/unread-count'),
    markAllRead: () => apiClient.post('/portal-notifications/mark-all-read'),
  },

  // ──────────────── Centro de Notificaciones · auditoría (Solo Arsenal) ────────────────
  // Los filtros se mandan tal cual al backend: el CSV y la tabla comparten
  // parámetros para que la exportación no pueda diferir de lo que se ve.
  notificationAudit: {
    filters: () => apiClient.get('/notification-audit/filters'),
    list: (params = {}) => apiClient.get('/notification-audit/deliveries', { params }),
    detail: (id) => apiClient.get(`/notification-audit/deliveries/${id}`),
    retry: (id) => apiClient.post(`/notification-audit/deliveries/${id}/retry`),
    review: (id, reviewed = true) => apiClient.post(`/notification-audit/deliveries/${id}/review`, { reviewed }),
    matrix: (params = {}) => apiClient.get('/notification-audit/matrix', { params }),
    exportUrl: (params = {}) => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('access_token') || '';
      const query = new URLSearchParams(
        Object.entries(params).filter(([, value]) => value !== '' && value != null),
      ).toString();
      return { url: `${API_BASE_URL}/notification-audit/export${query ? `?${query}` : ''}`, token };
    },
  },

  // ───────────────────────── Uso del Sistema (Solo Arsenal) ─────────────────────────
  systemUsage: {
    overview: (days = 30) => apiClient.get(`/system-usage/overview?days=${days}`, { timeout: 120000 }),
    company: (companyId, days = 30) => apiClient.get(`/system-usage/companies/${companyId}?days=${days}`),
    user: (userId, days = 30) => apiClient.get(`/system-usage/users/${userId}?days=${days}`),
    exportUrl: (days = 30) => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('access_token') || '';
      return { url: `${API_BASE_URL}/system-usage/export?days=${days}`, token };
    },
    // Tracking de uso (best-effort, no bloquea nunca la UI).
    track: (module, action, extra = {}) => {
      try { apiClient.post('/system-usage/track', { module, action, ...extra }).catch(() => {}); } catch (e) { /* noop */ }
    },
  },

  // ───────────────────────── Campañas de adopción ─────────────────────────
  adoption: {
    // Usuario final (propias preferencias)
    getPreferences: () => apiClient.get('/adoption/preferences'),
    updatePreferences: (data) => apiClient.put('/adoption/preferences', data),
    // Solo Arsenal
    getConfig: () => apiClient.get('/adoption/config'),
    updateConfig: (data) => apiClient.put('/adoption/config', data),
    triggers: () => apiClient.get('/adoption/triggers'),
    listCampaigns: () => apiClient.get('/adoption/campaigns'),
    createCampaign: (data) => apiClient.post('/adoption/campaigns', data),
    updateCampaign: (id, data) => apiClient.put(`/adoption/campaigns/${id}`, data),
    deleteCampaign: (id) => apiClient.delete(`/adoption/campaigns/${id}`),
    generate: (prompt) => apiClient.post('/adoption/campaigns/generate', { prompt }),
    preview: (id) => apiClient.post(`/adoption/campaigns/${id}/preview`),
    test: (id) => apiClient.post(`/adoption/campaigns/${id}/test`),
    run: (id, dryRun = true) => apiClient.post(`/adoption/campaigns/${id}/run?dry_run=${dryRun}`),
    seed: () => apiClient.post('/adoption/seed'),
    deliveries: (limit = 100) => apiClient.get(`/adoption/deliveries?limit=${limit}`),
  },

  // Authentication with Multi-company Support
  auth: {
    register: (data) => {
      if (!data.admin_timezone) {
        data.admin_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City';
      }
      return apiClient.post('/auth/register-company', data);
    },

    login: (data, companyId = null) => {
      const formData = new FormData();
      formData.append('username', data.username || data.email);
      formData.append('password', data.password);
      if (companyId) {
        formData.append('company_id', companyId);
      }
      return apiClient.post('/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      }).then(res => {
        if (res.data && res.data.access_token) {
          localStorage.setItem('authToken', res.data.access_token);
          localStorage.setItem('access_token', res.data.access_token);
        }
        return res;
      });
    },

    switchCompany: (data) => apiClient.post('/auth/switch-company', data),
    getCurrentUser: () => apiClient.get('/auth/me'),
    getCompanies: () => apiClient.get('/auth/companies'),
    createSmartInvitation: (data) => apiClient.post('/auth/create-smart-invitation', data),
    createInvitation: (data) => apiClient.post('/auth/create-smart-invitation', data),
    createInvitationLegacy: (data) => apiClient.post('/auth/create-invitation-legacy', data),

    useInvitation: (data) => {
      if (!data.timezone) {
        data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City';
      }
      return apiClient.post('/auth/use-invitation', data);
    },

    createUserManually: (data) => {
      if (!data.timezone) {
        data.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City';
      }
      return apiClient.post('/auth/create-user-manual', data);
    },

    joinCompany: (invitationCode) => apiClient.post('/auth/join-company', { invitation_code: invitationCode }),

    getInvitations: () => apiClient.get('/auth/invitations'),
    cancelInvitation: (invitationCode) => apiClient.delete(`/auth/invitations/${invitationCode}`),
    getServicePlans: () => apiClient.get('/auth/service-plans'),
    createRegistrationLink: (data) => apiClient.post('/auth/registration-links', data),
    validateRegistrationLink: (token) => apiClient.get(`/auth/registration-links/${token}/validate`),
    validateRegistrationEmail: async (data) => {
      try {
        const response = await apiClient.post('/auth/validate-email', data);

        if (response.data?.exists && response.data?.user) {
          response.data.user = normalizeRegistrationLookupUser(response.data.user, data?.email);
        }

        return response;
      } catch (error) {
        if (error.response?.status === 404 || error.response?.status === 405) {
          try {
            const fallbackResponse = await apiClient.post('/password-reset/validate-email', data);

            return {
              ...fallbackResponse,
              data: {
                exists: true,
                user: normalizeRegistrationLookupUser(
                  {
                    email: data?.email,
                    full_name: fallbackResponse.data?.user_name,
                    language: 'es',
                    timezone: null,
                  },
                  data?.email
                )
              }
            };
          } catch (fallbackError) {
            if (fallbackError.response?.status === 404) {
              return {
                status: 200,
                data: { exists: false }
              };
            }
            throw fallbackError;
          }
        }

        throw error;
      }
    },
    validateEmail: (data) => apiClient.post('/password-reset/validate-email', data),
    requestPasswordReset: (data) => apiClient.post('/password-reset/request', data),
  },

  public: {
    clientCalculate: (data) => apiClient.post('/public/client-calculate', data),
  },

  // Companies
  companies: {
    getCurrent: () => apiClient.get('/companies/current'),
    updateCurrent: (data) => apiClient.put('/companies/current', data),
    getStats: () => apiClient.get('/companies/stats'),
    getUsers: () => apiClient.get('/companies/users'),
    getServicePlan: () => apiClient.get('/companies/service-plan'),
    getResourceLimits: () => apiClient.get('/companies/resource-limits'),
    getOverview: () => apiClient.get('/companies/overview'),
    getActivitySummary: (days = 7) => apiClient.get(`/companies/activity-summary?days=${days}`),
    getServicePlanUsage: () => apiClient.get('/companies/service-plan-usage'),
    getSubscriptionStatus: () => apiClient.get('/companies/subscription-status'),
    canAddResource: (resource) => apiClient.get(`/companies/can-add/${resource}`),
    getCommercialConfig: () => apiClient.get('/companies/commercial-config'),
    updateCommercialConfig: (data) => apiClient.put('/companies/commercial-config', data),
  },

  // Users API with editing and password reset
  users: {
    getProfile: () => apiClient.get('/users/me'),
    updateProfile: (data) => apiClient.put('/users/me', data),
    updatePassword: (data) => apiClient.put('/users/me/password', data),
    getAll: (includeDeleted = false) => apiClient.get(`/users/?include_deleted=${includeDeleted}`),
    getById: (id) => apiClient.get(`/users/${id}`),

    update: (id, data) => {
      if (data.timezone && !isValidTimezone(data.timezone)) {
        return Promise.reject(new Error('Invalid timezone'));
      }
      if (data.role && !['user', 'manager', 'admin', 'superadmin'].includes(data.role)) {
        return Promise.reject(new Error('Invalid role'));
      }
      return apiClient.put(`/users/${id}`, data);
    },

    delete: (id) => apiClient.delete(`/users/${id}`),
    restore: (id) => apiClient.post(`/users/${id}/restore`),
    getActivity: (id, limit = 50) => apiClient.get(`/users/${id}/activity?limit=${limit}`),
    resetPassword: (id, data) => apiClient.put(`/users/${id}/reset-password`, data),
    transferSuperadmin: (id, data) => apiClient.post(`/users/${id}/transfer-superadmin`, data),
  },

  // 🆕 NEW: Unit Preferences API
  unitPreferences: {
    // Get current user's unit preferences
    getMyPreferences: () => apiClient.get('/unit-preferences/'),

    // Update current user's unit preferences
    updateMyPreferences: (data) => apiClient.put('/unit-preferences/', data),

    // Reset preferences to defaults
    resetToDefaults: () => apiClient.post('/unit-preferences/reset'),

    // Get all available units by type
    getAvailableUnits: () => apiClient.get('/unit-preferences/available-units'),

    // Apply a predefined preset (metric, imperial, scientific)
    applyPreset: (presetName) => apiClient.post('/unit-preferences/apply-preset', null, {
      params: { preset_name: presetName }
    }),

    // Test unit conversion
    testConversion: (data) => apiClient.post('/unit-preferences/test-conversion', data),

    // Get conversion examples
    getConversionExamples: () => apiClient.get('/unit-preferences/conversion-examples'),
  },

  // Vehicles
  vehicles: {
    getAll: () => apiClient.get('/vehicles/'),
    getAllCompanies: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/vehicles/all-companies?${searchParams.toString()}`);
    },
    getById: (id) => apiClient.get(`/vehicles/${id}`),
    create: (data) => apiClient.post('/vehicles/', data),
    update: (id, data) => apiClient.put(`/vehicles/${id}`, data),
    delete: (id) => apiClient.delete(`/vehicles/${id}`),
    getConfigurations: () => apiClient.get('/vehicles/configurations'),
    assignSensor: (vehicleId, data) => apiClient.post(`/vehicles/${vehicleId}/sensors`, data),
    removeSensor: (vehicleId, assignmentId) => apiClient.delete(`/vehicles/${vehicleId}/sensors/${assignmentId}`),
    rotateSensors: (vehicleId, positionA, positionB) =>
      apiClient.post(`/vehicles/${vehicleId}/sensors/rotate`, {
        position_a: positionA,
        position_b: positionB,
      }),
  },

  // Sensors
  sensors: {
    getAll: () => apiClient.get('/sensors/'),
    getById: (id) => apiClient.get(`/sensors/${id}`),
    create: (data) => apiClient.post('/sensors/', data),
    update: (id, data) => apiClient.put(`/sensors/${id}`, data),
    delete: (id) => apiClient.delete(`/sensors/${id}`),
  },

  // Receivers
  receivers: {
    getAll: () => apiClient.get('/sensors/receivers/'),
    getById: (id) => apiClient.get(`/sensors/receivers/${id}`),
    create: (data) => apiClient.post('/sensors/receivers/', data),
    update: (id, data) => apiClient.put(`/sensors/receivers/${id}`, data),
    delete: (id) => apiClient.delete(`/sensors/receivers/${id}`),
    getEvents: (id) => apiClient.get(`/sensors/receivers/${id}/events`),
    sendCommand: (id, data) => apiClient.post(`/sensors/receivers/${id}/send-command`, data),
  },

  // GPS Devices
  gps: {
    getAll: () => apiClient.get('/sensors/gps/'),
    getById: (id) => apiClient.get(`/sensors/gps/${id}`),
    create: (data) => apiClient.post('/sensors/gps/', data),
    update: (id, data) => apiClient.put(`/sensors/gps/${id}`, data),
    delete: (id) => apiClient.delete(`/sensors/gps/${id}`),
  },

  // Telemetry
  telemetry: {
    getLatestBySensors: (vehicleId) => apiClient.get(`/telemetry/latest-by-vehicle/${vehicleId}`),
    getLatestMultipleVehicles: (vehicleIds = null) => {
      const params = vehicleIds ? `?vehicle_ids=${vehicleIds}` : '';
      return apiClient.get(`/telemetry/latest-by-vehicles${params}`);
    },
    getLatestMultipleVehiclesAllCompanies: (vehicleIds = null) => {
      const params = vehicleIds ? `?vehicle_ids=${vehicleIds}` : '';
      return apiClient.get(`/telemetry/latest-by-vehicles/all-companies${params}`);
    },

    getData: (params = {}) => {
      const cleanedParams = cleanParams(params);
      if (cleanedParams.start_date) {
        const startDate = new Date(cleanedParams.start_date);
        if (!cleanedParams.start_date.includes('T')) {
          startDate.setHours(0, 0, 0, 0);
        }
        cleanedParams.start_date = startDate.toISOString();
      }
      if (cleanedParams.end_date) {
        const endDate = new Date(cleanedParams.end_date);
        if (!cleanedParams.end_date.includes('T')) {
          endDate.setHours(23, 59, 59, 999);
        }
        cleanedParams.end_date = endDate.toISOString();
      }
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/telemetry/?${searchParams}`);
    },
    
    getStats: (hours = 24) => apiClient.get(`/telemetry/stats?hours=${hours}`),
    getVehicleLatest: (vehicleId) => apiClient.get(`/telemetry/vehicle/${vehicleId}/latest`),
    getVehiclePressure: (vehicleId, params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/telemetry/vehicle/${vehicleId}/pressure?${searchParams}`);
    },
    getVehicleTemperature: (vehicleId, params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/telemetry/vehicle/${vehicleId}/temperature?${searchParams}`);
    },
    getVehicleAccelerometer: (vehicleId, params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/telemetry/vehicle/${vehicleId}/accelerometer?${searchParams}`);
    },
    getDashboard: () => apiClient.get('/telemetry/dashboard'),
    export: (params = {}) => {
      const cleanedParams = cleanParams(params);
      if (cleanedParams.start_date) {
        const startDate = new Date(cleanedParams.start_date);
        if (!cleanedParams.start_date.includes('T')) {
          startDate.setHours(0, 0, 0, 0);
        }
        cleanedParams.start_date = startDate.toISOString();
      }
      if (cleanedParams.end_date) {
        const endDate = new Date(cleanedParams.end_date);
        if (!cleanedParams.end_date.includes('T')) {
          endDate.setHours(23, 59, 59, 999);
        }
        cleanedParams.end_date = endDate.toISOString();
      }
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/telemetry/export?${searchParams}`);
    },
    getAlerts: (hours = 24) => apiClient.get(`/telemetry/alerts?hours=${hours}`),
    processAlarms: () => apiClient.post('/telemetry/process-alarms'),
    processSingleAlarm: (telemetryId) => apiClient.post(`/telemetry/process-single-alarm/${telemetryId}`),
    getVehicleLastAvailable: (vehicleId) => apiClient.get(`/telemetry/vehicle/${vehicleId}/last-available`),
    getVehicleCombinedData: (vehicleId, includeLastAvailable = true) =>
      apiClient.get(`/telemetry/vehicle/${vehicleId}/combined-data?include_last_available=${includeLastAvailable}`),
    getDashboardTiles: () => apiClient.get('/telemetry/dashboard/vehicle-tiles'),

    getVehicleGPS: (vehicleId, params = {}) => {
    const cleanedParams = cleanParams(params);
    if (cleanedParams.start_date) {
      const startDate = new Date(cleanedParams.start_date);
      if (!cleanedParams.start_date.includes('T')) {
        startDate.setHours(0, 0, 0, 0);
      }
      cleanedParams.start_date = startDate.toISOString();
    }
    if (cleanedParams.end_date) {
      const endDate = new Date(cleanedParams.end_date);
      if (!cleanedParams.end_date.includes('T')) {
        endDate.setHours(23, 59, 59, 999);
      }
      cleanedParams.end_date = endDate.toISOString();
    }
    const searchParams = new URLSearchParams(cleanedParams);
    return apiClient.get(`/telemetry/vehicle/${vehicleId}/gps?${searchParams}`);
  },

  getLatestVehicleLocation: (vehicleId) => 
    apiClient.get(`/telemetry/vehicle/${vehicleId}/location/latest`),

  getVehicleSystemEvents: (vehicleId, params = {}) => {
    const cleanedParams = cleanParams(params);
    if (cleanedParams.start_date) {
      const startDate = new Date(cleanedParams.start_date);
      if (!cleanedParams.start_date.includes('T')) {
        startDate.setHours(0, 0, 0, 0);
      }
      cleanedParams.start_date = startDate.toISOString();
    }
    if (cleanedParams.end_date) {
      const endDate = new Date(cleanedParams.end_date);
      if (!cleanedParams.end_date.includes('T')) {
        endDate.setHours(23, 59, 59, 999);
      }
      cleanedParams.end_date = endDate.toISOString();
    }
    const searchParams = new URLSearchParams(cleanedParams);
    return apiClient.get(`/telemetry/vehicle/${vehicleId}/system-events?${searchParams}`);
  },

  getSystemEventTypes: () => 
    apiClient.get('/telemetry/system-events/types'),

  // Métodos de conveniencia para tipos específicos
  getVehiclePings: (vehicleId, params = {}) => {
    return api.telemetry.getVehicleSystemEvents(vehicleId, {
      ...params,
      event_types: 'PING'
    });
  },

  getVehiclePowerEvents: (vehicleId, params = {}) => {
    return api.telemetry.getVehicleSystemEvents(vehicleId, {
      ...params,
      event_types: 'POWEROFF,STARTUP'
    });
  },

  getVehicleConnectivityEvents: (vehicleId, params = {}) => {
    return api.telemetry.getVehicleSystemEvents(vehicleId, {
      ...params,
      event_types: 'HB ONLINE,HB OFFLINE'
    });
  },
  },

  // Alarms service (migrated to profiles)
  alarms: {
    getAll: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/alarms/?${searchParams}`);
    },
    getById: (id) => apiClient.get(`/alarms/${id}`),
    create: (data) => {
      const cleanedData = { ...data };
      if (cleanedData.threshold_min !== undefined) {
        cleanedData.threshold_min = cleanedData.threshold_min === '' || cleanedData.threshold_min === null
          ? null
          : parseFloat(cleanedData.threshold_min);
      }
      if (cleanedData.threshold_max !== undefined) {
        cleanedData.threshold_max = cleanedData.threshold_max === '' || cleanedData.threshold_max === null
          ? null
          : parseFloat(cleanedData.threshold_max);
      }
      return apiClient.post('/alarms/', cleanedData);
    },
    update: (id, data) => {
      const cleanedData = { ...data };
      if (cleanedData.threshold_min !== undefined) {
        cleanedData.threshold_min = cleanedData.threshold_min === '' || cleanedData.threshold_min === null
          ? null
          : parseFloat(cleanedData.threshold_min);
      }
      if (cleanedData.threshold_max !== undefined) {
        cleanedData.threshold_max = cleanedData.threshold_max === '' || cleanedData.threshold_max === null
          ? null
          : parseFloat(cleanedData.threshold_max);
      }
      return apiClient.put(`/alarms/${id}`, cleanedData);
    },
    delete: (id) => apiClient.delete(`/alarms/${id}`),
    deleteVehicleAlarms: (vehicleId, metricType) =>
      apiClient.delete(`/alarms/vehicle/${vehicleId}/metric/${metricType}`),
    getStatus: () => apiClient.get('/alarms/status/realtime'),
    getStatusAllCompanies: () => apiClient.get('/alarms/status/realtime/all-companies'),
    getActiveStatus: () => apiClient.get('/alarms/status/realtime'),
    getRealtimeStatus: () => apiClient.get('/alarms/status/realtime'),
    checkRecentTelemetry: (params = {}) => {
      const cleanedParams = cleanParams(params);
      return apiClient.post('/alarms/check/recent', cleanedParams);
    },
    testEmail: (data) => apiClient.post('/alarms/email/test', data),
    sendManualAlert: (alarmId) => apiClient.post(`/alarms/email/send-alert?alarm_id=${alarmId}`),
    getDashboardSummary: () => apiClient.get('/alarms/dashboard/summary'),
    bulkCreate: (data) => apiClient.post('/alarms/bulk-create', data),
    migrateToProfiles: () => apiClient.post('/alarms/migrate-to-profiles/'),
    getMigrationStatus: () => apiClient.get('/alarms/migration-status/'),
  },

  // Alarm Profiles API
  alarmProfiles: {
    getAll: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.active_only !== undefined) {
        queryParams.append('active_only', params.active_only);
      }
      if (params.include_templates !== undefined) {
        queryParams.append('include_templates', params.include_templates);
      }
      const url = `/alarm-profiles/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      return apiClient.get(url);
    },

    getById: (id) => apiClient.get(`/alarm-profiles/${id}`),

    create: (data) => {
      return apiClient.post('/alarm-profiles/', serializeAlarmProfilePayload(data));
    },

    update: (id, data) => {
        const updateData = {};

        if (data.name !== undefined) updateData.name = data.name;
        if (data.description !== undefined) updateData.description = data.description;
        if (data.active !== undefined) updateData.active = Boolean(data.active);
        if (data.axle_count !== undefined) updateData.axle_count = parseInt(data.axle_count, 10);
        if (data.applies_to_all_axles !== undefined) updateData.applies_to_all_axles = Boolean(data.applies_to_all_axles);
        if (data.tpms_settings !== undefined || data.tpmsSettings !== undefined) {
          updateData.tpms_settings = data.tpms_settings || data.tpmsSettings;
        }
        if (data.configurations !== undefined) {
          updateData.configurations = serializeAlarmProfilePayload(data).configurations;
        }

        return apiClient.put(`/alarm-profiles/${id}`, updateData);
    },

    delete: (id) => apiClient.delete(`/alarm-profiles/${id}`),

    duplicate: (id, newName) => {
      const params = new URLSearchParams({ new_name: newName });
      return apiClient.post(`/alarm-profiles/${id}/duplicate?${params.toString()}`);
    },

    getAssignments: async (params = {}) => {
      try {
        const queryParams = new URLSearchParams();
        if (params.vehicle_id) queryParams.append('vehicle_id', params.vehicle_id);
        if (params.profile_id) queryParams.append('profile_id', params.profile_id);
        if (params.active_only !== undefined) queryParams.append('active_only', params.active_only);

        const url = `/alarm-profiles/assignments/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
        const response = await apiClient.get(url);

        if (response.data && Array.isArray(response.data)) {
          response.data.forEach((assignment, index) => {
            if (!assignment.profile) {
              console.warn(`Missing profile object in assignment ${assignment.id}`);
            } else if (!assignment.profile.configurations || assignment.profile.configurations.length === 0) {
              console.warn(`Missing configurations in profile ${assignment.profile.id}`);
            }
          });
        }

        return response;
      } catch (error) {
        console.error('Error calling alarm profiles assignments API:', error);
        throw error;
      }
    },

    assignToVehicle: (data) => {
      const assignmentData = {
        vehicle_id: data.vehicle_id,
        profile_id: data.profile_id,
        active: Boolean(data.active)
      };
      return apiClient.post('/alarm-profiles/assignments/', assignmentData);
    },

    removeAssignment: (assignmentId) => apiClient.delete(`/alarm-profiles/assignments/${assignmentId}`),
    checkVehicleCompatibility: (vehicleId) => apiClient.get(`/alarm-profiles/compatibility/vehicle/${vehicleId}`),
    getSystemTemplates: () => apiClient.get('/alarm-profiles/?active_only=true&include_templates=true'),
    // Plantilla de tpms_settings (defaults globales administrados desde el panel Arsenal)
    getProfileTemplate: () => apiClient.get('/tpms-defaults/profile-template'),

    bulkAssign: async (assignments) => {
      const results = [];
      const errors = [];

      for (const assignment of assignments) {
        try {
          const result = await api.alarmProfiles.assignToVehicle(assignment);
          results.push(result);
        } catch (error) {
          errors.push({
            assignment,
            error: error.message || 'Unknown error'
          });
        }
      }

      return {
        success: results,
        errors: errors,
        total: assignments.length,
        successful: results.length,
        failed: errors.length
      };
    },

    debug: {
      async validateAssignmentStructure(vehicleId) {
        try {
          const response = await api.alarmProfiles.getAssignments({ vehicle_id: vehicleId });
          return response;
        } catch (error) {
          console.error('Assignment structure validation failed:', error);
          throw error;
        }
      },

      async testFullPipeline(vehicleId) {
        try {
          const assignments = await api.alarmProfiles.getAssignments({ vehicle_id: vehicleId });
          
          if (assignments.data && assignments.data.length > 0) {
            const firstAssignment = assignments.data[0];
            if (firstAssignment.profile?.configurations) {
              firstAssignment.profile.configurations.forEach((config, i) => {
                console.log(`Config ${i + 1}:`, {
                  axle: config.axle_number,
                  metric: config.metric_type,
                  min: config.threshold_min,
                  max: config.threshold_max,
                  unit: config.unit,
                  enabled: config.enabled
                });
              });
            }
          }

          return {
            success: true,
            assignments: assignments.data?.length || 0,
            configurations: assignments.data?.[0]?.profile?.configurations?.length || 0
          };
        } catch (error) {
          return {
            success: false,
            error: error.message
          };
        }
      }
    }
  },

  // Audit
  audit: {
    getLogs: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/audit/logs?${searchParams}`);
    },
    getLogById: (id) => apiClient.get(`/audit/logs/${id}`),
    getEntityTrail: (entity, entityId) => apiClient.get(`/audit/entity/${entity}/${entityId}`),
    getStats: (days = 30) => apiClient.get(`/audit/stats?days=${days}`),
    getUserActivity: (userId, limit = 100) => apiClient.get(`/audit/user/${userId}/activity?limit=${limit}`),
    export: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/audit/export?${searchParams}`);
    },
    getActionTypes: () => apiClient.get('/audit/action-types'),
    getEntities: () => apiClient.get('/audit/entities'),
  },

  // Billing System API
  billing: {
    // Access check
    checkAccess: () => apiClient.get('/billing/access-check'),

    // Plans management
    createPlan: (data) => apiClient.post('/billing/plans', data),
    listPlans: (activeOnly = false) => apiClient.get(`/billing/plans?active_only=${activeOnly}`),
    getPlan: (planId, includeConfig = true) => apiClient.get(`/billing/plans/${planId}?include_config=${includeConfig}`),
    updatePlan: (planId, data) => apiClient.put(`/billing/plans/${planId}`, data),
    deletePlan: (planId) => apiClient.delete(`/billing/plans/${planId}`),
    createStandardPlans: () => apiClient.post('/billing/plans/create-standard'),

    // Plan configuration
    addConfigItem: (planId, data) => apiClient.post(`/billing/plans/${planId}/config`, data),
    getPlanConfig: (planId) => apiClient.get(`/billing/plans/${planId}/config`),
    updateConfigItem: (configId, data) => apiClient.put(`/billing/config/${configId}`, data),
    deleteConfigItem: (configId) => apiClient.delete(`/billing/config/${configId}`),

    // Company plan assignments
    assignPlanToCompany: (companyId, data) => apiClient.post(`/billing/companies/${companyId}/assign-plan`, data),
    getCompanyActivePlan: (companyId) => apiClient.get(`/billing/companies/${companyId}/active-plan`),
    getCompanyPlanHistory: (companyId) => apiClient.get(`/billing/companies/${companyId}/plan-history`),
    getCompanyCommercialConfig: (companyId) => apiClient.get(`/billing/companies/${companyId}/commercial-config`),
    updateCompanyCommercialConfig: (companyId, data) => apiClient.put(`/billing/companies/${companyId}/commercial-config`, data),

    // Calculation and preview
    calculateCharges: (companyId, periodStart, periodEnd) => apiClient.post('/billing/calculate-charges', null, {
      params: { company_id: companyId, period_start: periodStart, period_end: periodEnd }
    }),

    // Invoices
    generateInvoice: (data) => apiClient.post('/billing/invoices/generate', data),
    generateMonthlyInvoices: (targetMonth = null) => {
      const params = targetMonth ? { target_month: targetMonth } : {};
      return apiClient.post('/billing/invoices/generate-monthly', null, { params });
    },
    listInvoices: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/billing/invoices?${searchParams}`);
    },
    getInvoice: (invoiceId) => apiClient.get(`/billing/invoices/${invoiceId}`),
    updateInvoiceStatus: (invoiceId, newStatus, notes = null) => {
      const params = { new_status: newStatus };
      if (notes) params.notes = notes;
      return apiClient.put(`/billing/invoices/${invoiceId}/status`, null, { params });
    },
    getOverdueInvoices: () => apiClient.get('/billing/invoices/overdue/list'),
    markOverdueInvoices: () => apiClient.post('/billing/invoices/overdue/mark'),

    // Payments
    recordPayment: (invoiceId, data) => apiClient.post(`/billing/invoices/${invoiceId}/payments`, data),

    // History and audit
    getEntityHistory: (entityType, entityId, limit = 100) => apiClient.get(`/billing/history/${entityType}/${entityId}?limit=${limit}`),
    getAuditTrail: (entityType, entityId) => apiClient.get(`/billing/history/audit-trail/${entityType}/${entityId}`),
    getRecentHistory: (entityType = null, limit = 50) => {
      const params = entityType ? `?entity_type=${entityType}&limit=${limit}` : `?limit=${limit}`;
      return apiClient.get(`/billing/history/recent${params}`);
    },

    // Notes
    createNote: (data) => apiClient.post('/billing/notes', data),
    getCompanyNotes: (companyId) => apiClient.get(`/billing/notes/${companyId}`),

    // Stats and reports
    getStats: () => apiClient.get('/billing/stats/overview'),

    // Self billing portal
    getSelfSummary: () => apiClient.get('/billing/self/summary'),
    createStripeCustomer: () => apiClient.post('/billing/self/stripe/customer'),
    createSetupIntent: () => apiClient.post('/billing/self/stripe/setup-intent'),
    listPaymentMethods: () => apiClient.get('/billing/self/stripe/payment-methods'),
    setDefaultPaymentMethod: (paymentMethodId) => apiClient.post(`/billing/self/stripe/payment-methods/${paymentMethodId}/default`),
    detachPaymentMethod: (paymentMethodId) => apiClient.delete(`/billing/self/stripe/payment-methods/${paymentMethodId}`),
    openStripePortal: () => apiClient.post('/billing/self/stripe/portal'),
    listManualPayments: () => apiClient.get('/billing/self/manual-payments'),
    createManualPayment: (formData) => apiClient.post('/billing/self/manual-payments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

    // Arsenal admin manual payment review
    adminListManualPayments: (statusFilter = null) => {
      const params = statusFilter ? `?status_filter=${encodeURIComponent(statusFilter)}` : '';
      return apiClient.get(`/billing/admin/manual-payments${params}`);
    },
    adminApproveManualPayment: (paymentId, notes = null) => {
      const form = new FormData();
      if (notes) form.append('notes', notes);
      return apiClient.post(`/billing/admin/manual-payments/${paymentId}/approve`, form);
    },
    adminRejectManualPayment: (paymentId, notes = null) => {
      const form = new FormData();
      if (notes) form.append('notes', notes);
      return apiClient.post(`/billing/admin/manual-payments/${paymentId}/reject`, form);
    },
  },

  // Admin (Arsenal personnel only)
  admin: {
    // TPMS Global Defaults (Panel Arsenal)
    getTPMSDefaults: () => apiClient.get('/admin/tpms-defaults/'),
    updateTPMSDefault: (key, value) => apiClient.put(`/admin/tpms-defaults/${encodeURIComponent(key)}`, { value }),

    // Companies management
    getAllCompanies: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/admin/companies/all?${searchParams}`);
    },

    // Products & Categories (already exist in admin endpoints)
    getCategories: (includeInactive = false) => apiClient.get(`/admin/categories?include_inactive=${includeInactive}`),
    createCategory: (data) => apiClient.post('/admin/categories', data),
    updateCategory: (categoryId, data) => apiClient.put(`/admin/categories/${categoryId}`, data),
    deleteCategory: (categoryId) => apiClient.delete(`/admin/categories/${categoryId}`),

    getProducts: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/admin/products?${searchParams}`);
    },
    createProduct: (data) => apiClient.post('/admin/products', data),
    updateProduct: (productId, data) => apiClient.put(`/admin/products/${productId}`, data),
    deleteProduct: (productId) => apiClient.delete(`/admin/products/${productId}`),
    
    getKits: (includeInactive = false) => apiClient.get(`/admin/kits?include_inactive=${includeInactive}`),
    createKit: (data) => apiClient.post('/admin/kits', data),
    updateKit: (kitId, data) => apiClient.put(`/admin/kits/${kitId}`, data),
    deleteKit: (kitId) => apiClient.delete(`/admin/kits/${kitId}`),

    getOrders: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/admin/orders?${searchParams}`);
    },
    updateOrderStatus: (orderId, data) => apiClient.patch(`/admin/orders/${orderId}/status`, data),
    addShippingUpdate: (orderId, data) => apiClient.post(`/admin/orders/${orderId}/shipping-update`, data),
    
    getCoupons: () => apiClient.get('/admin/coupons'),
    createCoupon: (data) => apiClient.post('/admin/coupons', data),
    updateCoupon: (couponId, data) => apiClient.put(`/admin/coupons/${couponId}`, data),
    deleteCoupon: (couponId) => apiClient.delete(`/admin/coupons/${couponId}`),
    validateCoupon: (code) => apiClient.get(`/admin/coupons/validate/${code}`),

    getDashboard: () => apiClient.get('/admin/analytics/dashboard'),

    // Demo Alarm System
    runAlarmDemo: (adminEmail = null, adminPhone = null) => {
      const params = new URLSearchParams();
      if (adminEmail) params.append('admin_email', adminEmail);
      if (adminPhone) params.append('admin_phone', adminPhone);
      return apiClient.post(`/admin/demo/run-alarm-demo?${params}`);
    },

    runAlarmDemoStep: (step, payload = {}) => {
      const params = new URLSearchParams();
      params.append('step', step);
      return apiClient.post(`/admin/demo/guided/step?${params.toString()}`, payload);
    },

    getDemoDetail: (companyId) => {
      const params = new URLSearchParams();
      if (companyId) params.append('company_id', companyId);
      return apiClient.get(`/admin/demo/detail?${params.toString()}`);
    },

    cleanupDemo: (companyId = null) => {
      const params = new URLSearchParams();
      if (companyId) params.append('company_id', companyId);
      return apiClient.post(`/admin/demo/cleanup?${params}`);
    },

    demoStatus: () => apiClient.get('/admin/demo/status'),
  },

  // Alarm Tickets API
  alarmTickets: {
    // Busca a qué empresa del usuario pertenece un ticket dado (sin filtrar por la
    // empresa activa). Permite hacer switch automático al abrir un link de otra empresa.
    lookupCompany: (ticketId) => apiClient.get(`/alarm-tickets/${ticketId}/company-lookup`),
    getAll: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/alarm-tickets/?${searchParams}`);
    },

    getById: (id) => apiClient.get(`/alarm-tickets/${id}`),
    getDeliveries: (ticketId) => apiClient.get(`/alarm-tickets/${ticketId}/deliveries`),
    // Intentos de cierre automático: explica por qué un ticket sigue abierto
    // aunque el PSI actual parezca en rango (mejora por temperatura).
    getResolutionAttempts: (ticketId) =>
      apiClient.get(`/alarm-tickets/${ticketId}/resolution-attempts`),
    getActiveForAudio: () => apiClient.get('/alarm-tickets/audio/active'),
    getStats: () => apiClient.get('/alarm-tickets/stats'),

    acknowledge: (ticketIds) => {
      const data = Array.isArray(ticketIds) ? { ticket_ids: ticketIds } : { ticket_ids: [ticketIds] };
      return apiClient.post('/alarm-tickets/acknowledge', data);
    },

    acknowledgeVehicle: (vehicleId) => apiClient.post(`/alarm-tickets/acknowledge-vehicle/${vehicleId}`),

    resolve: (ticketId, resolutionNotes) => {
      return apiClient.put(`/alarm-tickets/${ticketId}/resolve`, {
        resolution_notes: resolutionNotes
      });
    },

    getWithStats: (params = {}) => {
      const cleanedParams = cleanParams({
        ...params,
        include_stats: 'true'
      });
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/alarm-tickets/?${searchParams}`);
    },

    getByStatus: (status, params = {}) => {
      const cleanedParams = cleanParams({
        ...params,
        status_filter: status
      });
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/alarm-tickets/?${searchParams}`);
    },

    getByVehicle: (vehicleId, params = {}) => {
      const cleanedParams = cleanParams({
        ...params,
        vehicle_id: vehicleId
      });
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/alarm-tickets/?${searchParams}`);
    },

    getOpen: () => apiClient.get('/alarm-tickets/?status_filter=open'),
    getAcknowledged: () => apiClient.get('/alarm-tickets/?status_filter=acknowledged'),
    getResolved: () => apiClient.get('/alarm-tickets/?status_filter=resolved'),

    // Centro de acción del ticket (tareas / chat / WhatsApp / captura / IA)
    ticketSummary: (id) => apiClient.get(`/alarm-tickets/${id}/summary`),
    ticketTasks: (id) => apiClient.get(`/alarm-tickets/${id}/tasks`),
    ticketTaskSuggestions: (id) => apiClient.get(`/alarm-tickets/${id}/tasks/suggestions`),
    createTicketTask: (id, data) => apiClient.post(`/alarm-tickets/${id}/tasks`, data),
    // Tareas ya relacionadas con el ticket (se muestran en su detalle).
    getTicketTasks: (id) => apiClient.get(`/alarm-tickets/${id}/tasks`),
    ticketChannels: (id) => apiClient.get(`/alarm-tickets/${id}/chat-channels`),
    createTicketChannel: (id, data) => apiClient.post(`/alarm-tickets/${id}/chat-channel`, data),
    whatsappMessage: (id, data = {}) => apiClient.post(`/alarm-tickets/${id}/whatsapp-message`, data),
    ticketTriggerCalculation: (id) => apiClient.get(`/alarm-tickets/${id}/trigger-calculation`),
    ticketAiAnalysis: (id) => apiClient.get(`/alarm-tickets/${id}/ai-analysis`, { timeout: 90000 }),
    regenTicketAiAnalysis: (id) => apiClient.post(`/alarm-tickets/${id}/ai-analysis`, {}, { timeout: 90000 }),
    ticketCollaboration: (id) => apiClient.get(`/alarm-tickets/${id}/collaboration`),
  },

  // Operadores / Choferes — entidad sin login, asignable a vehículos (máx 2).
  operators: {
    list: (params = {}) => {
      const cleaned = cleanParams(params);
      const sp = new URLSearchParams(cleaned);
      const qs = sp.toString();
      return apiClient.get(`/operators/${qs ? `?${qs}` : ''}`);
    },
    get: (id) => apiClient.get(`/operators/${id}`),
    create: (data) => apiClient.post('/operators/', data),
    update: (id, data) => apiClient.put(`/operators/${id}`, data),
    delete: (id) => apiClient.delete(`/operators/${id}`),

    // Asignación a vehículos
    listForVehicle: (vehicleId) => apiClient.get(`/operators/vehicles/${vehicleId}`),
    // Payload de UI: cupo del vehículo, conteo, etiquetas y teléfono normalizado.
    vehicleSummary: (vehicleId) => apiClient.get(`/operators/vehicles/${vehicleId}/summary`),
    // Alta rápida: crea el operador y lo asigna al vehículo en una sola operación.
    createAndAssign: (vehicleId, data) =>
      apiClient.post(`/operators/vehicles/${vehicleId}/create-and-assign`, data),
    // Perspectiva inversa: vehículos donde está asignado un operador.
    listVehiclesForOperator: (operatorId) => apiClient.get(`/operators/${operatorId}/vehicles`),
    // `force` mueve al operador si ya estaba en otro vehículo (el backend
    // responde 409 la primera vez para que la UI pueda confirmar).
    assignToVehicle: (vehicleId, operatorId, slot = null, { force = false } = {}) =>
      apiClient.post(`/operators/vehicles/${vehicleId}/assign`, {
        operator_id: operatorId,
        ...(slot ? { slot } : {}),
        ...(force ? { force_move: true } : {}),
      }),
    unassignFromVehicle: (vehicleId, operatorId) =>
      apiClient.delete(`/operators/vehicles/${vehicleId}/unassign/${operatorId}`),
    replaceForVehicle: (vehicleId, operatorIds) =>
      apiClient.put(`/operators/vehicles/${vehicleId}/replace`, {
        operator_ids: operatorIds || [],
      }),
  },

  // Vista operativa de flota (lista + mapa). El backend calcula todo.
  fleet: {
    getOverview: (params = {}) =>
      apiClient.get('/fleet/overview', { params: cleanParams(params), timeout: 120000 }),
    getMap: () => apiClient.get('/fleet/map', { timeout: 60000 }),
    getVehicleDetail: (vehicleId, params = {}) =>
      apiClient.get(`/fleet/vehicle/${vehicleId}/detail`, { params: cleanParams(params), timeout: 90000 }),
  },

  // Geocercas multiempresa (vehículos, equipos, usuarios, sensores, receptores, GPS…).
  geofences: {
    getMetadata: () => apiClient.get('/geofences/metadata'),
    getAll: (params = {}) => apiClient.get('/geofences', { params: cleanParams(params) }),
    getById: (id) => apiClient.get(`/geofences/${id}`),
    create: (data) => apiClient.post('/geofences', data),
    update: (id, data) => apiClient.put(`/geofences/${id}`, data),
    remove: (id, hard = false) => apiClient.delete(`/geofences/${id}`, { params: { hard } }),
    setEntities: (id, entities) => apiClient.put(`/geofences/${id}/entities`, entities),
    addEntity: (id, entity) => apiClient.post(`/geofences/${id}/entities`, entity),
    removeEntity: (id, linkId) => apiClient.delete(`/geofences/${id}/entities/${linkId}`),
    getEvents: (id, params = {}) =>
      apiClient.get(`/geofences/${id}/events`, { params: cleanParams(params) }),
    getRecentEvents: (params = {}) =>
      apiClient.get('/geofences/events/recent', { params: cleanParams(params) }),
    getEntityState: (entityType, entityId) =>
      apiClient.get(`/geofences/state/${entityType}/${entityId}`),
  },

  // Horarios operativos: gobiernan si se generan alarmas de no reporte.
  operatingSchedules: {
    getAll: () => apiClient.get('/operating-schedules'),
    create: (data) => apiClient.post('/operating-schedules', data),
    update: (id, data) => apiClient.put(`/operating-schedules/${id}`, data),
    remove: (id) => apiClient.delete(`/operating-schedules/${id}`),
    resolveForVehicle: (vehicleId) => apiClient.get(`/operating-schedules/resolve/${vehicleId}`),
  },

  reports: {
    getTPMSSettings: () => apiClient.get('/reports/tpms/settings'),
    updateTPMSSettings: (data) => apiClient.put('/reports/tpms/settings', data),

    getActionSummary: () => apiClient.get('/reports/tpms/action-summary', { timeout: 120000 }),
    // Presión equivalente en frío por posición (motor central, mismo cálculo que los correos).
    getColdPressure: (vehicleIds) =>
      apiClient.get('/reports/tpms/cold-pressure', { params: { vehicle_ids: vehicleIds }, timeout: 60000 }),

    getTPMSOverview: (params = {}) => {
      const cleanedParams = cleanParams(params);
      if (cleanedParams.start_date && !String(cleanedParams.start_date).includes('T')) {
        cleanedParams.start_date = `${cleanedParams.start_date}T00:00:00Z`;
      }
      if (cleanedParams.end_date && !String(cleanedParams.end_date).includes('T')) {
        cleanedParams.end_date = `${cleanedParams.end_date}T23:59:59Z`;
      }
      if (cleanedParams.currency) {
        cleanedParams.currency = String(cleanedParams.currency).toUpperCase();
      }
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/reports/tpms/overview?${searchParams}`, { timeout: 600000 });
    },

    exportTPMS: (params = {}) => {
      const cleanedParams = cleanParams(params);
      if (cleanedParams.start_date && !String(cleanedParams.start_date).includes('T')) {
        cleanedParams.start_date = `${cleanedParams.start_date}T00:00:00Z`;
      }
      if (cleanedParams.end_date && !String(cleanedParams.end_date).includes('T')) {
        cleanedParams.end_date = `${cleanedParams.end_date}T23:59:59Z`;
      }
      if (cleanedParams.currency) {
        cleanedParams.currency = String(cleanedParams.currency).toUpperCase();
      }
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/reports/tpms/export?${searchParams}`, { responseType: 'blob', timeout: 600000 });
    },
  },

  // ========== Company Networks (WiFi) ==========
  companyNetworks: {
    list: () => apiClient.get('/company-networks'),
    create: (data) => apiClient.post('/company-networks', data),
    update: (id, data) => apiClient.put(`/company-networks/${id}`, data),
    markRegistered: (id, registered = true) => apiClient.post(`/company-networks/${id}/mark-registered`, { registered }),
    resendEmail: (id) => apiClient.post(`/company-networks/${id}/resend-email`),
    delete: (id) => apiClient.delete(`/company-networks/${id}`),

    // Overview combinado de receptores: registrados, pendientes, faltantes
    receiversOverview: () => apiClient.get('/company-networks/receivers-overview'),
    registerReceiver: (data) => apiClient.post('/company-networks/receivers/register', data),
    updateReceiver: (id, data) => apiClient.put(`/company-networks/receivers/${id}`, data),
  },

  // ========== Tasks module ==========
  tasks: {
    list: (params = {}) => {
      const cleaned = cleanParams(params);
      const sp = new URLSearchParams(cleaned);
      return apiClient.get(`/tasks${sp.toString() ? '?' + sp.toString() : ''}`);
    },
    get: (id) => apiClient.get(`/tasks/${id}`),
    // Indicador del navbar: contadores + muestra de mis pendientes.
    myPending: (limit = 8) => apiClient.get(`/tasks/my-pending?limit=${limit}`),
    // Plantilla del mensaje (WhatsApp/correo) y envío por el canal elegido.
    messageTemplate: (id, kind = 'assigned') =>
      apiClient.get(`/tasks/${id}/message-template?kind=${kind}`),
    notify: (id, data) => apiClient.post(`/tasks/${id}/notify`, data),
    create: (data) => apiClient.post('/tasks', data),
    update: (id, data) => apiClient.put(`/tasks/${id}`, data),
    transition: (id, status, note = '') => apiClient.post(`/tasks/${id}/transition`, { status, note }),
    comment: (id, body, mentions = []) => apiClient.post(`/tasks/${id}/comments`, { body, mentions }),
    delete: (id) => apiClient.delete(`/tasks/${id}`),
    dashboard: () => apiClient.get('/tasks/dashboard'),
  },

  taskRules: {
    list: (activeOnly = false) => apiClient.get(`/task-rules${activeOnly ? '?active_only=true' : ''}`),
    get: (id) => apiClient.get(`/task-rules/${id}`),
    create: (data) => apiClient.post('/task-rules', data),
    update: (id, data) => apiClient.put(`/task-rules/${id}`, data),
    toggle: (id) => apiClient.post(`/task-rules/${id}/toggle`),
    delete: (id) => apiClient.delete(`/task-rules/${id}`),
    executions: (id, limit = 50) => apiClient.get(`/task-rules/${id}/executions?limit=${limit}`),
  },

  // ========== Chat module ==========
  chat: {
    listChannels: () => apiClient.get('/chat/channels'),
    createChannel: (data) => apiClient.post('/chat/channels', data),
    // Abre (o reabre) el chat directo con un usuario. Idempotente en backend:
    // pulsar dos veces lleva a la MISMA conversación, no crea una segunda.
    openDirect: (userId) => apiClient.post('/chat/direct', { user_id: userId }),
    getChannel: (id) => apiClient.get(`/chat/channels/${id}`),
    deleteChannel: (id) => apiClient.delete(`/chat/channels/${id}`),
    canDelete: (id) => apiClient.get(`/chat/channels/${id}/can-delete`),
    listMessages: (channelId, params = {}) => {
      const sp = new URLSearchParams(cleanParams(params));
      return apiClient.get(`/chat/channels/${channelId}/messages${sp.toString() ? '?' + sp.toString() : ''}`);
    },
    postMessage: (channelId, body, mentions = [], entityLinks = [], clientMessageId = null, replyToMessageId = null) =>
      apiClient.post(`/chat/channels/${channelId}/messages`, {
        body, mentions, entity_links: entityLinks,
        // Idempotencia: el backend devuelve el MISMO mensaje si se reenvía el mismo id.
        ...(clientMessageId ? { client_message_id: clientMessageId } : {}),
        // Responder/citar: referencia al mensaje original (uuid).
        ...(replyToMessageId ? { reply_to_message_id: replyToMessageId } : {}),
      }),
    // Alterna (toggle) la reacción del usuario actual sobre un mensaje.
    // Responde {ok, action:'added'|'removed', reactions:[...]} y difunde por WS.
    toggleReaction: (messageId, emoji) =>
      apiClient.post(`/chat/messages/${messageId}/reactions`, { emoji }),
    // Indicador "escribiendo…": difunde chat_typing por WS. Emitir con throttle.
    sendTyping: (channelId) =>
      apiClient.post(`/chat/channels/${channelId}/typing`),
    markRead: (channelId, lastMessageId = null) =>
      apiClient.post(`/chat/channels/${channelId}/read`, { last_message_id: lastMessageId }),
    // Recibos de entrega/lectura por miembro del canal (estilo WhatsApp ✓/✓✓).
    // Devuelve { receipts: [ {user_id, user_name, last_read_at, last_read_message_id,
    //   last_delivered_at, last_delivered_message_id} ] }. Fechas ISO o null.
    getReceipts: (channelId) => apiClient.get(`/chat/channels/${channelId}/receipts`),
    // Marca ENTREGADO hasta lastMessageId (úsalo cuando el cliente RECIBE mensajes
    // de otros). Difunde WS chat_delivered. NO usar para mensajes propios.
    markDelivered: (channelId, lastMessageId) =>
      apiClient.post(`/chat/channels/${channelId}/delivered`, { last_message_id: lastMessageId }),
    unread: () => apiClient.get('/chat/unread'),
    autocomplete: (q, kind = null, limit = 8, channelId = null) => {
      const sp = new URLSearchParams({ q, limit });
      if (kind) sp.append('kind', kind);
      if (channelId) sp.append('channel_id', channelId);
      return apiClient.get(`/chat/mentions/autocomplete?${sp.toString()}`);
    },
    autocompleteGrouped: (q, limitPerKind = 5, channelId = null) => {
      const sp = new URLSearchParams({ q, limit_per_kind: limitPerKind });
      if (channelId) sp.append('channel_id', channelId);
      return apiClient.get(`/chat/mentions/autocomplete-grouped?${sp.toString()}`);
    },

    // Adjuntos / notas de voz / notas de video (multipart). files: File[]|Blob[]
    // Campos opcionales del backend: waveform (JSON array 0..1), width, height,
    // attachment_kind ('image'|'voice'|'video_note'|'file').
    postAttachments: (channelId, files, {
      body = '', isVoice = false, durationSeconds = null, onProgress, clientMessageId = null,
      waveform = null, width = null, height = null, attachmentKind = null, replyToMessageId = null,
    } = {}) => {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f, f.name || (isVoice ? 'nota-de-voz.webm' : 'archivo')));
      fd.append('body', body || '');
      fd.append('is_voice', isVoice ? 'true' : 'false');
      if (durationSeconds != null) fd.append('duration_seconds', String(Math.round(durationSeconds)));
      if (Array.isArray(waveform) && waveform.length) fd.append('waveform', JSON.stringify(waveform));
      if (width != null) fd.append('width', String(Math.round(width)));
      if (height != null) fd.append('height', String(Math.round(height)));
      if (attachmentKind) fd.append('attachment_kind', attachmentKind);
      // Idempotencia: evita adjuntos duplicados ante doble click o reintento.
      if (clientMessageId) fd.append('client_message_id', clientMessageId);
      // Responder/citar sobre un adjunto.
      if (replyToMessageId) fd.append('reply_to_message_id', replyToMessageId);
      return apiClient.post(`/chat/channels/${channelId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
        onUploadProgress: onProgress,
      });
    },
    downloadAttachment: (attachmentId) =>
      apiClient.get(`/chat/attachments/${attachmentId}/download`, { responseType: 'blob', timeout: 120000 }),
    // URL directa autenticada por query (?token=) para usar en <audio>/<video> src.
    // El navegador no envía el header Authorization en estos elementos.
    attachmentStreamUrl: (attachmentId) => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('access_token') || '';
      const base = `${API_BASE_URL}/chat/attachments/${attachmentId}/download`;
      return token ? `${base}?token=${encodeURIComponent(token)}` : base;
    },

    // Web Push
    pushPublicKey: () => apiClient.get('/chat/push/public-key'),
    pushSubscribe: (subscription) => apiClient.post('/chat/push/subscribe', { subscription }),
    pushUnsubscribe: (endpoint = null) => apiClient.post('/chat/push/unsubscribe', { endpoint }),

    // Llamadas WebRTC
    iceServers: () => apiClient.get('/chat/calls/ice-servers'),
    activeCall: (channelId) => apiClient.get(`/chat/channels/${channelId}/calls/active`),
    startCall: (channelId, callType = 'audio', targetUserId = null) =>
      apiClient.post(`/chat/channels/${channelId}/calls`, { call_type: callType, target_user_id: targetUserId }),
    joinCall: (callId) => apiClient.post(`/chat/calls/${callId}/join`),
    leaveCall: (callId) => apiClient.post(`/chat/calls/${callId}/leave`),
    rejectCall: (callId) => apiClient.post(`/chat/calls/${callId}/reject`),
    // Terminar la llamada para TODOS (permitido a quien inició o al creador del canal).
    endCall: (callId) => apiClient.post(`/chat/calls/${callId}/end`),

    // Preferencias de notificación (globales + por canal)
    getPreferences: () => apiClient.get('/chat/preferences'),
    updatePreferences: (data) => apiClient.put('/chat/preferences', data),
    setChannelNotifications: (channelId, mode, muteMinutes = null) =>
      apiClient.put(`/chat/channels/${channelId}/notifications`, { mode, mute_minutes: muteMinutes }),
  },

  notificationPreferences: {
    getMyPreferences: () => apiClient.get('/notification-preferences/me'),
    updateMyPreferences: (data) => apiClient.put('/notification-preferences/me', data),
    testNotifications: (data) => apiClient.post('/notification-preferences/test', data),
    getUserPreferences: (userId) => apiClient.get(`/notification-preferences/user/${userId}`),
    updateUserPreferences: (userId, data) => apiClient.put(`/notification-preferences/user/${userId}`, data),
    getCompanySummary: () => apiClient.get('/notification-preferences/company/summary'),
    // Matriz empresa x usuario x evento x canal. Sin `company_id` el backend
    // responde con la empresa activa; 'all' solo lo acepta a Arsenal.
    channelMatrix: (params = {}) => apiClient.get('/notification-preferences/channel-matrix', { params }),
    getServiceStatus: () => apiClient.get('/notification-preferences/service-status')
  },

  // 🔄 UPDATED: Enhanced Sync API with all new endpoints from sync.py
  sync: {
    // Status and control
    getStatus: () => apiClient.get('/sync/status'),
    start: () => apiClient.post('/sync/start'),
    stop: () => apiClient.post('/sync/stop'),

    // 🆕 NEW: Enhanced metrics and monitoring
    getMetrics: () => apiClient.get('/sync/metrics'),
    health: () => apiClient.get('/sync/health'),
    getRealTimeStatus: () => apiClient.get('/sync/real-time-status'),
    getDetailedStatus: () => apiClient.get('/sync/debug/detailed-status'),

    // 🆕 NEW: Data types statistics
    getDataTypesStats: (hoursBack = 24) => apiClient.get(`/sync/data-types-stats?hours_back=${hoursBack}`),

    // Integrity checking
    performIntegrityCheck: (params = {}) => apiClient.post('/sync/integrity-check', params),

    // Recovery actions
    forceCompleteSync: () => apiClient.post('/sync/force-complete-sync'),
    emergencyIntegrityFix: () => apiClient.post('/sync/emergency-integrity-fix'),

    // Data comparison (Amazon vs PostgreSQL)
    getDataComparison: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/sync/data-comparison?${searchParams}`);
    },

    // 🆕 NEW: Debug utilities
    debugMissingRecords: (timestamps) => apiClient.post('/sync/debug/missing-records', { timestamps }),

    // 🆕 NEW: Schema validation
    validateSchema: (sampleSize = 1000) => apiClient.get(`/sync/validate-schema?sample_size=${sampleSize}`),

    // System administration
    resetSystem: () => apiClient.post('/sync/reset-system'),

    // Legacy methods maintained for compatibility
    resetMetrics: () => apiClient.post('/sync/reset-system'),
    getValidationStats: () => apiClient.get('/sync/debug/detailed-status'),
    getRecentData: (minutes = 30) => apiClient.get(`/sync/real-time-status?minutes=${minutes}`),

    // Deprecated methods but maintained
    manual: (data = {}) => {
      console.warn('sync.manual is deprecated - continuous sync runs automatically');
      return Promise.resolve({ data: { message: 'Manual sync not needed with continuous sync' } });
    },
    toggleGapDetection: (enable = true) => {
      console.warn('Gap detection is now automatic in continuous sync');
      return Promise.resolve({ data: { message: 'Gap detection is automatic' } });
    },
    forceGapRecovery: () => apiClient.post('/sync/force-complete-sync'),
    checkAlarms: (data = {}) => apiClient.post('/alarms/status/realtime'),
    forceAlarmCheck: () => apiClient.post('/alarms/status/realtime'),
    getAlarmMetrics: () => apiClient.get('/alarms/dashboard/summary'),
  },

  // Notifications
  notifications: {
    pollAlarms: () => apiClient.get('/alarms/status/realtime'),
    pollTelemetry: () => apiClient.get('/telemetry/dashboard'),
    getPreferences: () => apiClient.get('/notifications/preferences'),
    updatePreferences: (data) => apiClient.put('/notifications/preferences', data),
    getHistory: (params = {}) => {
      const cleanedParams = cleanParams(params);
      const searchParams = new URLSearchParams(cleanedParams);
      return apiClient.get(`/notifications/history?${searchParams}`);
    },
    markAsRead: (notificationIds) => apiClient.put('/notifications/mark-read', { notification_ids: notificationIds }),
    markAllAsRead: () => apiClient.put('/notifications/mark-all-read'),
  },

  // System Health
  system: {
    getHealth: () => apiClient.get('/health'),
    getMetrics: () => apiClient.get('/system/metrics'),
    getAlarmHealth: () => apiClient.get('/system/alarm-health'),
    getEmailHealth: () => apiClient.get('/system/email-health'),
  },

  // Add profileUtils to api object
  profileUtils
};

// Enhanced timezone utilities with dynamic loading from JSON
export const timezoneHelpers = {
  _timezonesCache: null,

  async loadTimezones() {
    if (this._timezonesCache) {
      return this._timezonesCache;
    }

    try {
      const response = await fetch('/countries-master/json/timezones.json');
      if (!response.ok) {
        throw new Error('Failed to load timezones');
      }

      const timezones = await response.json();

      this._timezonesCache = timezones.map(tz => ({
        value: tz.value,
        label: tz.text,
        offset: tz.offset,
        abbr: tz.abbr,
        isDST: tz.isdst,
        utcIdentifiers: tz.utc || [tz.value],
        standardName: tz.value
      })).sort((a, b) => {
        if (a.offset !== b.offset) {
          return a.offset - b.offset;
        }
        return a.label.localeCompare(b.label);
      });

      return this._timezonesCache;
    } catch (error) {
      console.error('Error loading timezones:', error);
      return this.getFallbackTimezones();
    }
  },

  getFallbackTimezones() {
    return [
      { value: 'America/Mexico_City', label: '(UTC-06:00) Ciudad de México', offset: -6 },
      { value: 'America/New_York', label: '(UTC-05:00) Nueva York', offset: -5 },
      { value: 'America/Chicago', label: '(UTC-06:00) Chicago', offset: -6 },
      { value: 'America/Los_Angeles', label: '(UTC-08:00) Los Ángeles', offset: -8 },
      { value: 'America/Bogota', label: '(UTC-05:00) Bogotá', offset: -5 },
      { value: 'Europe/Madrid', label: '(UTC+01:00) Madrid', offset: 1 },
      { value: 'Europe/London', label: '(UTC+00:00) Londres', offset: 0 },
      { value: 'Asia/Tokyo', label: '(UTC+09:00) Tokio', offset: 9 },
      { value: 'UTC', label: '(UTC+00:00) UTC', offset: 0 }
    ];
  },

  async findTimezoneByValue(value) {
    const timezones = await this.loadTimezones();
    return timezones.find(tz =>
      tz.value === value ||
      (tz.utcIdentifiers || []).includes(value) ||
      tz.standardName === value
    );
  },

  async getTimezonesGroupedByRegion() {
    const timezones = await this.loadTimezones();

    const grouped = {
      'Americas': [],
      'Europe': [],
      'Asia': [],
      'Africa': [],
      'Australia': [],
      'Pacific': [],
      'Other': []
    };

    timezones.forEach(tz => {
      const value = tz.value;
      if (value.startsWith('America/')) {
        grouped.Americas.push(tz);
      } else if (value.startsWith('Europe/')) {
        grouped.Europe.push(tz);
      } else if (value.startsWith('Asia/')) {
        grouped.Asia.push(tz);
      } else if (value.startsWith('Africa/')) {
        grouped.Africa.push(tz);
      } else if (value.startsWith('Australia/')) {
        grouped.Australia.push(tz);
      } else if (value.startsWith('Pacific/')) {
        grouped.Pacific.push(tz);
      } else {
        grouped.Other.push(tz);
      }
    });

    return grouped;
  },

  getUserTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'America/Mexico_City';
    }
  },

  isValidTimezone(timezone) {
    try {
      Intl.DateTimeFormat(undefined, { timeZone: timezone });
      return true;
    } catch {
      return false;
    }
  },

  formatInTimezone(date, timezone, options = {}) {
    try {
      return new Intl.DateTimeFormat('es-MX', {
        timeZone: timezone,
        ...options
      }).format(new Date(date));
    } catch {
      return new Date(date).toLocaleString();
    }
  },

  getTimezoneOffset(timezone) {
    try {
      const now = new Date();
      const utc = new Date(now.getTime() + (now.getTimezoneOffset() * 60000));
      const target = new Date(utc.toLocaleString('en-US', { timeZone: timezone }));
      return (target.getTime() - utc.getTime()) / (1000 * 60 * 60);
    } catch {
      return 0;
    }
  },

  getCommonTimezones() {
    console.warn('getCommonTimezones is deprecated. Use loadTimezones() instead.');
    return this.getFallbackTimezones();
  }
};

// 🆕 ENHANCED: Unit Preferences Helpers
export const unitPreferencesHelpers = {
  /**
   * Get display name for unit type
   */
  getUnitTypeDisplayName(unitType) {
    const names = {
      'pressure': i18n.t('hardcoded.services_api.pressure'),
      'temperature': i18n.t('hardcoded.services_api.temperature'),
      'acceleration': i18n.t('hardcoded.services_api.acceleration'),
      'speed': i18n.t('hardcoded.services_api.speed')
    };
    return names[unitType] || unitType;
  },

  /**
   * Get icon for unit type
   */
  getUnitTypeIcon(unitType) {
    const icons = {
      'pressure': 'Speed',
      'temperature': 'Thermostat',
      'acceleration': 'TrendingUp',
      'speed': 'Speed'
    };
    return icons[unitType] || 'Settings';
  },

  /**
   * Format value with unit and decimal places
   */
  formatValueWithUnit(value, unit, decimalPlaces = 2) {
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }
    
    const numDecimals = parseInt(decimalPlaces) || 2;
    const formattedValue = parseFloat(value).toFixed(numDecimals);
    
    return `${formattedValue} ${unit}`;
  },

  /**
   * Get unit symbol for display
   */
  getUnitSymbol(unit) {
    const symbols = {
      // Pressure
      'PSI': 'PSI',
      'BAR': 'bar',
      'kPa': 'kPa',
      
      // Temperature
      'C': '°C',
      'F': '°F', 
      'K': 'K',
      
      // Acceleration
      'G': 'g',
      'm/s2': 'm/s²',
      'ft/s2': 'ft/s²',
      
      // Speed
      'km/h': 'km/h',
      'mph': 'mph',
      'm/s': 'm/s',
      'knots': 'kn'
    };
    return symbols[unit] || unit;
  },

  /**
   * Validate decimal places value
   */
  validateDecimalPlaces(value) {
    const num = parseInt(value);
    return !isNaN(num) && num >= 0 && num <= 6;
  },

  /**
   * Apply unit preferences to telemetry data
   */
  applyPreferencesToData(data, preferences) {
    if (!data || !preferences) return data;
    
    const convertedData = { ...data };
    const decimalMap = preferences.decimal_places_map || {};
    const globalDecimals = preferences.decimal_places;
    const pressureDecimals = Number.isInteger(decimalMap.pressure) ? decimalMap.pressure : globalDecimals;
    const temperatureDecimals = Number.isInteger(decimalMap.temperature) ? decimalMap.temperature : 1;
    
    // Apply conversions based on preferences
    if (convertedData.pressure !== undefined && convertedData.pressure !== null) {
      convertedData.pressure = this.formatValueWithUnit(
        convertedData.pressure, 
        preferences.pressure_unit, 
        pressureDecimals
      );
    }
    
    if (convertedData.temperature !== undefined && convertedData.temperature !== null) {
      convertedData.temperature = this.formatValueWithUnit(
        convertedData.temperature, 
        preferences.temperature_unit, 
        temperatureDecimals
      );
    }
    
    return convertedData;
  },

  /**
   * Get preset configurations
   */
  getPresetConfigurations() {
    return {
      metric: {
        name: i18n.t('hardcoded.services_api.preset_metric_name'),
        description: i18n.t('hardcoded.services_api.preset_metric_desc'),
        settings: {
          pressure_unit: 'kPa',
          temperature_unit: 'C',
          acceleration_unit: 'm/s2',
          speed_unit: 'km/h',
          use_imperial_system: false
        }
      },
      imperial: {
        name: i18n.t('hardcoded.services_api.preset_imperial_name'),
        description: i18n.t('hardcoded.services_api.preset_imperial_desc'),
        settings: {
          pressure_unit: 'PSI',
          temperature_unit: 'F',
          acceleration_unit: 'ft/s2',
          speed_unit: 'mph',
          use_imperial_system: true
        }
      },
      scientific: {
        name: i18n.t('hardcoded.services_api.preset_scientific_name'),
        description: i18n.t('hardcoded.services_api.preset_scientific_desc'),
        settings: {
          pressure_unit: 'BAR',
          temperature_unit: 'K',
          acceleration_unit: 'm/s2',
          speed_unit: 'm/s',
          use_imperial_system: false
        }
      }
    };
  }
};

// Enhanced user helpers with new functionalities
export const userHelpers = {
  async createInvitation(invitationData) {
    try {
      const response = await api.auth.createInvitation(invitationData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || i18n.t('hardcoded.services_api.failed_create_invitation')
      };
    }
  },

  async createUserManually(userData) {
    try {
      const response = await api.auth.createUserManually(userData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || i18n.t('hardcoded.services_api.failed_create_user')
      };
    }
  },

  async getInvitations() {
    try {
      const response = await api.auth.getInvitations();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || i18n.t('hardcoded.services_api.failed_get_invitations')
      };
    }
  },

  async cancelInvitation(invitationCode) {
    try {
      const response = await api.auth.cancelInvitation(invitationCode);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to cancel invitation'
      };
    }
  },

  async editUser(userId, userData) {
    try {
      const response = await api.users.update(userId, userData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to update user'
      };
    }
  },

  async resetUserPassword(userId, resetData) {
    try {
      const response = await api.users.resetPassword(userId, resetData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to reset password'
      };
    }
  },

  async transferSuperadmin(userId, transferData) {
    try {
      const response = await api.users.transferSuperadmin(userId, transferData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to transfer superadmin role'
      };
    }
  },

  async reInviteUser(originalInvitation) {
    try {
      const response = await api.auth.createInvitation({
        invite_email: originalInvitation.invite_email,
        user_type: originalInvitation.user_type,
        expires_in_days: 7
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to re-invite user'
      };
    }
  },

  validateUserData(userData) {
    const errors = [];

    if (!userData.email || !this.validateEmail(userData.email)) {
      errors.push(i18n.t('hardcoded.services_api.valid_email_required'));
    }

    if (!userData.name || userData.name.trim().length < 2) {
      errors.push(i18n.t('hardcoded.services_api.name_min_length'));
    }

    if (!userData.last_name || userData.last_name.trim().length < 2) {
      errors.push(i18n.t('hardcoded.services_api.last_name_min_length'));
    }

    if (userData.timezone && !timezoneHelpers.isValidTimezone(userData.timezone)) {
      errors.push(i18n.t('hardcoded.services_api.invalid_timezone'));
    }

    if (userData.role && !['user', 'manager', 'admin', 'superadmin'].includes(userData.role)) {
      errors.push(i18n.t('hardcoded.services_api.invalid_role'));
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  },

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  validateInvitationCode(invitationCode) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(invitationCode);
  },

  canUserModifyRole(currentUserRole, targetRole) {
    const roleHierarchy = {
      'superadmin': 4,
      'admin': 3,
      'manager': 2,
      'user': 1
    };

    const currentLevel = roleHierarchy[currentUserRole] || 0;
    const targetLevel = roleHierarchy[targetRole] || 0;

    return currentLevel > targetLevel && targetRole !== 'superadmin';
  },

  canUserEditUser(currentUserRole, targetUserRole) {
    const roleHierarchy = {
      'superadmin': 4,
      'admin': 3,
      'manager': 2,
      'user': 1
    };

    const currentLevel = roleHierarchy[currentUserRole] || 0;
    const targetLevel = roleHierarchy[targetUserRole] || 0;

    if (currentUserRole === 'superadmin') {
      return targetUserRole !== 'superadmin';
    }

    return currentLevel > targetLevel;
  },

  getUserRoleColor(role) {
    const colors = {
      'superadmin': 'secondary',
      'admin': 'error',
      'manager': 'warning',
      'user': 'default'
    };
    return colors[role] || 'default';
  },

  getUserRoleIcon(role) {
    const icons = {
      'superadmin': 'SupervisorAccount',
      'admin': 'AdminPanelSettings',
      'manager': 'ManageAccounts',
      'user': 'Person'
    };
    return icons[role] || 'Person';
  }
};

// Multi-company helper functions
export const multiCompanyHelpers = {
  async switchCompany(companyId) {
    try {
      const response = await api.auth.switchCompany({ company_id: companyId });

      if (response.data && response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        if (response.data.companies) {
          localStorage.setItem('companies', JSON.stringify(response.data.companies));
        }

        return { success: true, data: response.data };
      }

      return { success: false, error: i18n.t('hardcoded.services_api.failed_switch_company') };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || i18n.t('hardcoded.services_api.failed_switch_company')
      };
    }
  },

  async getUserCompanies() {
    try {
      const response = await api.auth.getCompanies();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || i18n.t('hardcoded.services_api.failed_get_companies')
      };
    }
  },

  async createSmartInvitation(invitationData) {
    try {
      const response = await api.auth.createSmartInvitation(invitationData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || i18n.t('hardcoded.services_api.failed_create_invitation')
      };
    }
  },

  async joinCompany(invitationCode, userData = null) {
    try {
      const requestData = { invitation_code: invitationCode };
      if (userData) {
        Object.assign(requestData, userData);
      }

      const response = await api.auth.useInvitation(requestData);

      if (response.data && response.data.access_token) {
        localStorage.setItem('authToken', response.data.access_token);
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));

        if (response.data.companies) {
          localStorage.setItem('companies', JSON.stringify(response.data.companies));
        }

        return { success: true, data: response.data };
      }

      return { success: false, error: i18n.t('hardcoded.services_api.failed_join_company') };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || i18n.t('hardcoded.services_api.failed_join_company')
      };
    }
  },

  hasAccessToCompany(companyId) {
    const companies = JSON.parse(localStorage.getItem('companies') || '[]');
    return companies.some(c => c.company_id === companyId);
  },

  getCurrentCompany() {
    const currentCompany = localStorage.getItem('currentCompany');
    return currentCompany ? JSON.parse(currentCompany) : null;
  },

  getRoleInCompany(companyId) {
    const companies = JSON.parse(localStorage.getItem('companies') || '[]');
    const company = companies.find(c => c.company_id === companyId);
    return company ? company.role : null;
  }
};

// Alarm helpers
export const alarmHelpers = {
  async hasActiveAlarms(vehicleId) {
    try {
      const response = await api.alarms.getActiveStatus();
      const alarms = response.data || [];
      return alarms.some(alarm =>
        alarm.vehicle_id === vehicleId &&
        (alarm.status === 'CRITICAL' || alarm.status === 'WARNING')
      );
    } catch (error) {
      console.error('Error checking active alarms:', error);
      return false;
    }
  },

  async getAlarmCounts() {
    try {
      const response = await api.alarms.getDashboardSummary();
      return response.data || {
        total_alarms: 0,
        active_alarms: 0,
        critical_alarms: 0,
        warning_alarms: 0,
        ok_alarms: 0
      };
    } catch (error) {
      console.error('Error getting alarm counts:', error);
      return {
        total_alarms: 0,
        active_alarms: 0,
        critical_alarms: 0,
        warning_alarms: 0,
        ok_alarms: 0
      };
    }
  },

  async sendTestEmail(email) {
    try {
      const response = await api.alarms.testEmail({ test_email: email });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to send test email'
      };
    }
  },

  async triggerAlarmCheck(minutes = 5) {
    try {
      const response = await api.alarms.checkRecentTelemetry({ minutes });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to check recent telemetry'
      };
    }
  }
};

// Helper functions for handling API responses
export const handleApiError = (error) => {
  if (error.response) {
    const detail = error.response.data?.detail;

    if (Array.isArray(detail)) {
      const messages = detail.map((d) => d.msg).join(', ');
      return { error: true, message: messages };
    }

    return {
      error: true,
      message: typeof detail === 'string' ? detail : i18n.t('hardcoded.services_api.an_error_occurred'),
      status: error.response.status
    };
  }

  if (error.request) {
    return {
      error: true,
      message: i18n.t('hardcoded.services_api.network_error'),
      status: 0
    };
  }

  return {
    error: true,
    message: error.message || i18n.t('hardcoded.services_api.unexpected_error'),
    status: 0
  };
};

export const isApiSuccess = (response) => {
  return response && response.status >= 200 && response.status < 300;
};

// Date range helpers for telemetry queries
export const dateHelpers = {
  calculateDateRange: (rangeType) => {
    const now = new Date();
    const endDate = new Date(now);
    const startDate = new Date(now);

    switch (rangeType) {
      case '1day':
        startDate.setDate(now.getDate() - 1);
        break;
      case '1week':
        startDate.setDate(now.getDate() - 7);
        break;
      case '1month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case '6months':
        startDate.setMonth(now.getMonth() - 6);
        break;
      case '1year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate.setDate(now.getDate() - 1);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      startDateTime: startDate.toISOString(),
      endDateTime: endDate.toISOString()
    };
  },

  dateToISO: (dateString, isEndDate = false) => {
    const date = new Date(dateString);
    if (isEndDate) {
      date.setHours(23, 59, 59, 999);
    } else {
      date.setHours(0, 0, 0, 0);
    }
    return date.toISOString();
  },

  formatDateForDisplay: (dateString) => {
    return new Date(dateString).toLocaleDateString();
  },

  getCurrentDate: () => {
    return new Date().toISOString().split('T')[0];
  },

  getDateDaysAgo: (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
  }
};

// Receiver helpers
export const receiverHelpers = {
  macToDynamoId(macAddress) {
    return macAddress.replace(/:/g, '').toUpperCase();
  },

  validateMacAddress(macAddress) {
    const macPattern = /^([0-9A-Fa-f]{2}[:]){5}([0-9A-Fa-f]{2})$/;
    return macPattern.test(macAddress);
  },

  formatMacAddress(value) {
    const clean = value.replace(/[^0-9A-Fa-f]/g, '');
    const formatted = clean.match(/.{1,2}/g)?.join(':') || clean;
    return formatted.substring(0, 17).toUpperCase();
  },

  async createReceiver(receiverData) {
    try {
      if (!this.validateMacAddress(receiverData.mac_address)) {
        return {
          success: false,
          error: 'Invalid MAC address format'
        };
      }

      const response = await api.receivers.create(receiverData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to create receiver'
      };
    }
  },

  async getAllReceivers() {
    try {
      const response = await api.receivers.getAll();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to get receivers'
      };
    }
  }
};

// Ticket Helpers
export const ticketHelpers = {
  async getTicketCounts() {
    try {
      const response = await api.alarmTickets.getStats();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to get ticket counts'
      };
    }
  },

  async vehicleHasOpenTickets(vehicleId) {
    try {
      const response = await api.alarmTickets.getByVehicle(vehicleId, { status_filter: 'open' });
      return {
        success: true,
        hasOpenTickets: response.data?.tickets?.length > 0,
        count: response.data?.tickets?.length || 0
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to check vehicle tickets'
      };
    }
  },

  async getAudioTickets() {
    try {
      const response = await api.alarmTickets.getActiveForAudio();
      return {
        success: true,
        tickets: response.data || [],
        openCount: response.data?.filter(t => t.status === 'open').length || 0
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to get audio tickets'
      };
    }
  },

  async bulkProcessTickets(ticketIds, action = 'acknowledge', resolutionNotes = null) {
    try {
      const results = [];
      const errors = [];

      for (const ticketId of ticketIds) {
        try {
          let result;
          if (action === 'acknowledge') {
            result = await api.alarmTickets.acknowledge([ticketId]);
          } else if (action === 'resolve' && resolutionNotes) {
            result = await api.alarmTickets.resolve(ticketId, resolutionNotes);
          } else {
            throw new Error('Invalid action or missing resolution notes');
          }

          results.push({ ticketId, success: true, data: result.data });
        } catch (error) {
          errors.push({
            ticketId,
            error: error.response?.data?.detail || error.message
          });
        }
      }

      return {
        success: true,
        results: results,
        errors: errors,
        successCount: results.length,
        errorCount: errors.length
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to process tickets'
      };
    }
  },

  async acknowledgeAllVehicleTickets(vehicleId) {
    try {
      const response = await api.alarmTickets.acknowledgeVehicle(vehicleId);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to acknowledge vehicle tickets'
      };
    }
  },

  async getTicketDetails(ticketId) {
    try {
      const response = await api.alarmTickets.getById(ticketId);
      return { success: true, ticket: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to get ticket details'
      };
    }
  },

  async searchTickets(filters = {}) {
    try {
      const params = {
        include_stats: true,
        limit: filters.limit || 50,
        offset: filters.offset || 0
      };

      if (filters.status) params.status_filter = filters.status;
      if (filters.vehicleId) params.vehicle_id = filters.vehicleId;
      if (filters.metricType) params.metric_type = filters.metricType;
      if (filters.priority) params.priority = filters.priority;

      const response = await api.alarmTickets.getAll(params);
      return {
        success: true,
        tickets: response.data?.tickets || [],
        stats: response.data?.stats || null,
        totalCount: response.data?.total_count || 0
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to search tickets'
      };
    }
  },

  formatDuration(minutes) {
    if (minutes < 60) return `${minutes}m`;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours < 24) {
      return `${hours}h ${remainingMinutes}m`;
    }

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  },

  getStatusColor(status) {
    const colors = {
      'open': 'error',
      'acknowledged': 'warning',
      'resolved': 'success'
    };
    return colors[status] || 'default';
  },

  getStatusLabel(status) {
    const labels = {
      'open': i18n.t('hardcoded.services_api.ticket_open'),
      'acknowledged': i18n.t('hardcoded.services_api.ticket_acknowledged'),
      'resolved': i18n.t('hardcoded.services_api.ticket_resolved')
    };
    return labels[status] || status;
  },

  validateTicketAction(ticket, action) {
    if (!ticket) {
      return { valid: false, error: i18n.t('hardcoded.services_api.ticket_not_found') };
    }

    if (action === 'acknowledge') {
      if (ticket.status !== 'open') {
        return { valid: false, error: i18n.t('hardcoded.services_api.ticket_only_open_can_acknowledge') };
      }
    }

    if (action === 'resolve') {
      if (ticket.status === 'resolved') {
        return { valid: false, error: i18n.t('hardcoded.services_api.ticket_already_resolved') };
      }
    }

    return { valid: true };
  }
};

// Hook for alarm profiles
export const useAlarmProfiles = () => {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadProfiles = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.alarmProfiles.getAll(params);
      setProfiles(response.data || []);
    } catch (err) {
      setError(err.message || t('hardcoded.pages_alarmprofilespage.error_al_cargar_perfiles'));
      console.error(t('hardcoded.services_api.error_loading_profiles'), err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAssignments = useCallback(async (params = {}) => {
    try {
      const response = await api.alarmProfiles.getAssignments(params);
      setAssignments(response.data || []);

      if (response.data) {
        response.data.forEach((assignment, index) => {
          if (!assignment.profile) {
            console.warn(`Assignment ${index + 1} missing profile object`);
          } else if (!assignment.profile.configurations || assignment.profile.configurations.length === 0) {
            console.warn(`Assignment ${index + 1} profile missing configurations`);
          }
        });
      }

    } catch (err) {
      console.error(t('hardcoded.services_api.error_loading_assignments'), err);
    }
  }, []);

  const createProfile = useCallback(async (profileData) => {
    try {
      setLoading(true);
      const response = await api.alarmProfiles.create(profileData);
      await loadProfiles();
      return response.data;
    } catch (err) {
      setError(err.message || t('hardcoded.pages_alarmprofilespage.error_al_crear_perfil'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadProfiles]);

  const debugAssignments = useCallback(async (vehicleId) => {
    if (!vehicleId) {
      return;
    }

    try {
      const result = await api.alarmProfiles.debug.testFullPipeline(vehicleId);
      return result;
    } catch (error) {
      console.error(t('hardcoded.services_api.debug_failed'), error);
      return { success: false, error: error.message };
    }
  }, []);

  return {
    profiles,
    assignments,
    loading,
    error,
    loadProfiles,
    loadAssignments,
    createProfile,
    debugAssignments
  };
};

// Default export
export default apiClient;